import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import {
    putFileToPresignedUrl,
    resolveFileContentType
} from '../utils/s3Upload'
import {
    extractSoPdfFieldsFromResponse,
    resolveSoPdfDownloadUrl
} from '../utils/soPdfDownload'

const API_URL = process.env.REACT_APP_API_URL
const CURRENCY_OPTIONS = ['INR', 'USD', 'EUR']
const FINAL_STATUS = 'Active'
const MAX_SO_OWNER_ATTACHMENTS = 10
const DEFAULT_GST = '18'
const GST_PERCENT_OPTIONS = ['0', '5', '18', '28']
const TAB_ORDER = ['details', 'materials', 'attachments', 'preview']

function normalizeGstPercent(value) {
  const gst = String(value ?? '').trim()
  if (GST_PERCENT_OPTIONS.includes(gst)) return gst
  const asNum = String(parseFloat(gst))
  if (GST_PERCENT_OPTIONS.includes(asNum)) return asNum
  return DEFAULT_GST
}

const DEFAULT_TERMS = [
    {
        term: 'Payment Terms',
        response: '3 days'
    },

]

const emptyTerm = () => ({ term: '', response: '' })

const getDefaultTerms = () =>
    DEFAULT_TERMS.map((row) => ({ term: row.term, response: row.response }))

const emptyMaterial = () => ({
    referenceNumber: '',
    referenceNumberId: '',
    materialCode: '',
    materialDescription: '',
    hsn: '',
    uom: '',
    quantity: '',
    cpoBasicPrice: '',
    gstPercent: DEFAULT_GST,
    gstValue: '',
    totalValue: '',
    technicalRemarks: ''
})

function parseApiResponse(raw) {
    let d = raw
    if (typeof d === 'string') {
        try {
            d = JSON.parse(d)
        } catch {
            return raw
        }
    }
    if (d?.body != null) {
        if (typeof d.body === 'string') {
            try {
                return JSON.parse(d.body)
            } catch {
                return d.body
            }
        }
        return d.body
    }
    return d
}

function isApiSuccess(responseData) {
    if (!responseData || typeof responseData !== 'object') return false
    const s = responseData.success
    return s === true || s === 'true' || s === 1 || s === '1'
}

function isValidEmailAddress(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
}

function parseAdditionalCcEmails(value) {
    return String(value || '')
        .split(/[,;]+/)
        .map((email) => email.trim())
        .filter(Boolean)
}

function buildCcEmails(userEmail, additionalInput, toEmail = '') {
    const result = []
    const seen = new Set()
    const toKey = String(toEmail || '').trim().toLowerCase()
    const addEmail = (email) => {
        const trimmed = String(email || '').trim()
        if (!trimmed) return
        const key = trimmed.toLowerCase()
        if (toKey && key === toKey) return
        if (seen.has(key)) return
        seen.add(key)
        result.push(trimmed)
    }

    addEmail(userEmail)
    parseAdditionalCcEmails(additionalInput).forEach(addEmail)
    return result
}

function getTodayIsoDate() {
    const now = new Date()
    const pad = (n) => String(n).padStart(2, '0')
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

function formatDateForApi(isoDate) {
    if (!isoDate) return ''
    const [yyyy, mm, dd] = isoDate.split('-')
    if (!yyyy || !mm || !dd) return ''
    return `${dd}-${mm}-${yyyy}`
}

function parseDateFromApi(value) {
    if (!value) return ''
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return value.slice(0, 10)
    }
    if (typeof value === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
        const [dd, mm, yyyy] = value.split('-')
        return `${yyyy}-${mm}-${dd}`
    }
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const pad = (n) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function formatDisplayDate(isoDate) {
    if (!isoDate) return '-'
    const [yyyy, mm, dd] = String(isoDate).split('-')
    if (!yyyy || !mm || !dd) return isoDate
    return `${dd}-${mm}-${yyyy}`
}

function calcMaterialPricing(cpoBasicPrice, quantity, gstPercent) {
    const basic = parseFloat(cpoBasicPrice) || 0
    const qty = parseFloat(quantity) || 0
    const gst = parseFloat(gstPercent) || 0
    const lineBasic = basic * qty
    const gstValue = (lineBasic * gst) / 100
    const totalValue = lineBasic + gstValue
    return { lineBasic, gstValue, totalValue }
}

function syncMaterialPricing(row) {
    const { gstValue, totalValue } = calcMaterialPricing(
        row.cpoBasicPrice,
        row.quantity,
        row.gstPercent
    )
    const basic = parseFloat(row.cpoBasicPrice) || 0
    const qty = parseFloat(row.quantity) || 0
    if (basic <= 0 || qty <= 0) {
        return { ...row, gstValue: '', totalValue: '' }
    }
    return {
        ...row,
        gstValue: gstValue.toFixed(2),
        totalValue: totalValue.toFixed(2)
    }
}

function calcGrossTotalsFromRows(rows) {
    return rows.reduce(
        (acc, row) => {
            const { lineBasic, gstValue, totalValue } = calcMaterialPricing(
                row.cpoBasicPrice,
                row.quantity,
                row.gstPercent
            )
            return {
                grossTotalBasicPrice: acc.grossTotalBasicPrice + lineBasic,
                grossGstValue: acc.grossGstValue + gstValue,
                grossTotalValue: acc.grossTotalValue + totalValue
            }
        },
        { grossTotalBasicPrice: 0, grossGstValue: 0, grossTotalValue: 0 }
    )
}

function formatAmount(value) {
    const num = Number(value)
    if (!Number.isFinite(num)) return '-'
    return num.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })
}

function isEmptyMaterialRow(row) {
    return (
        !String(row.materialCode || '').trim() &&
        !String(row.materialDescription || '').trim() &&
        !String(row.quantity || '').trim() &&
        !String(row.cpoBasicPrice || '').trim() &&
        !String(row.referenceNumber || '').trim()
    )
}

function extractCustomersList(payload) {
  const data = payload?.data != null ? payload.data : payload
  const list =
    (Array.isArray(data?.customers) && data.customers) ||
    (Array.isArray(data?.data) && data.data) ||
    (Array.isArray(payload?.customers) && payload.customers) ||
    (Array.isArray(data) && data) ||
    []
  return list
}

function normalizeRecordId(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'object') {
    if (value.$oid) return String(value.$oid).trim()
    if (value._id) return normalizeRecordId(value._id)
    if (value.id) return normalizeRecordId(value.id)
  }
  return String(value).trim()
}

function flattenCustomersToContactRows(customers) {
  const rows = []
  if (!Array.isArray(customers)) return rows

  customers.forEach((customer) => {
    const customerId = normalizeRecordId(customer?._id || customer?.id)
    const customerName = String(customer?.customerName || '').trim()
    const contacts = Array.isArray(customer?.contacts) ? customer.contacts : []

    if (contacts.length === 0) {
      const email = String(customer?.email || customer?.customerMailId || '').trim()
      const contactNumber = String(customer?.contactNumber || '').trim()
      if (!customerId || (!email && !contactNumber && !customerName)) return
      rows.push({
        key: `${customerId}|root`,
        customerId,
        contactId: '',
        customerName,
        contactPerson: '',
        designation: '',
        email,
        contactNumber
      })
      return
    }

    contacts.forEach((contact) => {
      const status = String(contact?.status || 'active').toLowerCase()
      if (status === 'inactive') return

      const contactId = normalizeRecordId(contact?._id || contact?.id)
      const email = String(contact?.email || '').trim()
      const contactNumber = String(contact?.contactNumber || '').trim()
      const contactPerson = String(contact?.contactPerson || '').trim()

      if (!customerId) return
      if (!email && !contactNumber && !contactPerson) return

      rows.push({
        key: `${customerId}|${contactId || email || contactNumber}`,
        customerId,
        contactId,
        customerName,
        contactPerson,
        designation: String(contact?.designation || '').trim(),
        email,
        contactNumber
      })
    })
  })

  return rows
}

const CUSTOMER_SEARCH_MIN_LENGTH = 2
const CUSTOMER_SEARCH_DEBOUNCE_MS = 400

function mapMaterialFromApi(m) {
    return syncMaterialPricing({
        referenceNumber: m.referenceNumber || '',
        referenceNumberId: String(m.referenceNumberId || m.referenceNumber_id || '').trim(),
        materialCode: m.materialCode || '',
        materialDescription: m.materialDescription || '',
        hsn: m.hsn || m.hsnCode || '',
        uom: m.uom || '',
        quantity: m.quantity != null ? String(m.quantity) : '',
        cpoBasicPrice: m.cpoBasicPrice != null ? String(m.cpoBasicPrice) : '',
        gstPercent: normalizeGstPercent(
          m.gstPercent != null && m.gstPercent !== ''
            ? m.gstPercent
            : m.gst != null && m.gst !== ''
              ? m.gst
              : DEFAULT_GST
        ),
        gstValue: m.gstValue != null ? String(m.gstValue) : '',
        totalValue: m.totalValue != null ? String(m.totalValue) : '',
        technicalRemarks: m.technicalRemarks || ''
    })
}

function mapOwnerFromApi(item) {
    return {
        s3Key: item?.s3Key || '',
        originalFileName: item?.originalFileName || '',
        contentType: item?.contentType || '',
        uploadedAt: item?.uploadedAt || '',
        url: item?.url || ''
    }
}

function extractSalesQuotesList(responseData) {
    let dataBlock = responseData?.data != null ? responseData.data : responseData
    if (typeof dataBlock === 'string') {
        try {
            dataBlock = JSON.parse(dataBlock)
        } catch {
            return []
        }
    }
    if (!dataBlock || typeof dataBlock !== 'object') return []
    if (Array.isArray(dataBlock)) return dataBlock
    const list = dataBlock.quotes || dataBlock.quoteList || dataBlock.items
    if (Array.isArray(list)) return list
    if (dataBlock._id || dataBlock.quoteNumber) return [dataBlock]
    return []
}

function extractCreatedId(responseData) {
    const data = responseData?.data
    if (!data) return null
    if (typeof data === 'string') return data
    return data.id || data._id || null
}

function sqMaterialToRow(material, quote) {
    const quoteNo = String(quote.quoteNumber || '').trim()
    const quoteId = String(quote._id || quote.id || '').trim()
    const basic =
        material.basicPrice != null
            ? material.basicPrice
            : material.cpoBasicPrice != null
                ? material.cpoBasicPrice
                : material.quotePrice != null
                    ? material.quotePrice
                    : ''
    const gst = normalizeGstPercent(
      material.gstPercent != null
        ? material.gstPercent
        : material.gst != null
          ? material.gst
          : DEFAULT_GST
    )
    return syncMaterialPricing({
      ...emptyMaterial(),
      referenceNumber: quoteNo,
      referenceNumberId: quoteId,
      materialCode: material.materialCode || '-',
      materialDescription: material.materialDescription || '',
      hsn: material.hsn || material.hsnCode || '-',
      uom: material.uom || '',
      quantity: material.quantity != null ? String(material.quantity) : '',
      cpoBasicPrice: basic !== '' ? String(basic) : '',
      gstPercent: gst,
      technicalRemarks: material.technicalRemarks || ''
    })
}

function getSqKey(item, index) {
    return item._id || item.id || `${item.quoteNumber || ''}|${index}`
}

function materialMatchesSq(material, quote) {
    if (!material || isEmptyMaterialRow(material)) return false
    const quoteId = String(quote._id || quote.id || '').trim()
    const matRefId = String(material.referenceNumberId || '').trim()
    if (quoteId && matRefId && quoteId === matRefId) return true
    const quoteNo = String(quote.quoteNumber || '').trim()
    const matRef = String(material.referenceNumber || '').trim()
    return Boolean(quoteNo && matRef && quoteNo.toLowerCase() === matRef.toLowerCase())
}

function isSqInMaterials(materials, quote) {
    return materials.some((m) => materialMatchesSq(m, quote))
}

const MATERIAL_SEARCH_TYPES = {
    pr: 'pr',
    sq: 'sq'
}

const MATERIAL_SEARCH_MIN_LENGTH = 3

const MATERIAL_SEARCH_DEFAULT_QUERIES = {
    [MATERIAL_SEARCH_TYPES.pr]: 'ITS-',
    [MATERIAL_SEARCH_TYPES.sq]: ''
}

function getMaterialSearchDefaultQuery(searchType) {
    return MATERIAL_SEARCH_DEFAULT_QUERIES[searchType] ?? MATERIAL_SEARCH_DEFAULT_QUERIES.pr
}

const getProcurementKey = (item, index) =>
    item._id || item.id || `${item.prNo || item.prNumber || ''}|${item.materialCode || ''}|${index}`

function materialMatchesProcurement(material, procurement) {
    if (!material || isEmptyMaterialRow(material)) return false
    const procId = String(procurement._id || procurement.id || '').trim()
    const matRefId = String(material.referenceNumberId || '').trim()
    if (procId && matRefId && procId === matRefId) return true
    const matRef = String(material.referenceNumber || '').trim()
    const procRef = String(procurement.prNo || procurement.prNumber || '').trim()
    const matCode = String(material.materialCode || '').trim()
    const procCode = String(procurement.materialCode || '').trim()
    return matRef && procRef && matRef === procRef && matCode && procCode && matCode === procCode
}

function isProcurementInMaterials(materials, procurement) {
    return materials.some((m) => materialMatchesProcurement(m, procurement))
}

function procurementToMaterialRow(procurement) {
    return syncMaterialPricing({
        ...emptyMaterial(),
        referenceNumber: String(procurement.prNo || procurement.prNumber || '').trim(),
        referenceNumberId: String(procurement._id || procurement.id || '').trim(),
        materialCode: procurement.materialCode || '-',
        materialDescription: procurement.materialDescription || '',
        hsn: procurement.hsnCode || procurement.hsn || '-',
        uom: procurement.uom || '',
        quantity:
            procurement.quantity != null && procurement.quantity !== ''
                ? String(procurement.quantity)
                : '',
        cpoBasicPrice: '',
        gstPercent: DEFAULT_GST,
        technicalRemarks: ''
    })
}

const isForwardMove = (fromTab, toTab) =>
    TAB_ORDER.indexOf(toTab) > TAB_ORDER.indexOf(fromTab)

const SalesOrderCreate = () => {
    const navigate = useNavigate()
    const { id: editId } = useParams()

    const [activeTab, setActiveTab] = useState('details')
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingOrder, setIsLoadingOrder] = useState(Boolean(editId))
    const [salesOrderId, setSalesOrderId] = useState(editId || null)
    const [recordStatus, setRecordStatus] = useState('Draft')
    const [cpoStatus, setCpoStatus] = useState('Draft')
    const [showPreviewStep, setShowPreviewStep] = useState(false)
    const [pdfLink, setPdfLink] = useState('')
    const [pdfDownloadUrl, setPdfDownloadUrl] = useState('')
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState('')
    const [isLoadingPdfPreview, setIsLoadingPdfPreview] = useState(false)
    const [userEmail, setUserEmail] = useState('')
    const [sendToEmail, setSendToEmail] = useState('')
    const [additionalCcEmails, setAdditionalCcEmails] = useState('')
    const [isSendingEmail, setIsSendingEmail] = useState(false)
    const [ccUserSuggestions, setCcUserSuggestions] = useState([])
    const [isSearchingCcUsers, setIsSearchingCcUsers] = useState(false)
    const ccSearchDebounceRef = useRef(null)
    const ccSearchRequestIdRef = useRef(0)

    const [orderDetails, setOrderDetails] = useState(() => ({
        customerName: '',
        customerId: '',
        customerNumber: '',
        customerMailId: '',
        cpoNumber: '',
        cpoDate: getTodayIsoDate(),
        deliverDate: '',
        currency: 'INR',
        cpoSummary: ''
    }))

    const [terms, setTerms] = useState(getDefaultTerms)
    const [materials, setMaterials] = useState([emptyMaterial()])

    const [customerSearch, setCustomerSearch] = useState('')
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false)
    const [customerSearchResults, setCustomerSearchResults] = useState([])
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
    const customerSearchDebounceRef = useRef(null)
    const customerSearchRequestIdRef = useRef(0)
    const customerSearchRef = useRef(null)
    const selectedCustomerIdRef = useRef('')
    const skipCustomerSearchClearRef = useRef(false)

    const [materialSearchType, setMaterialSearchType] = useState(MATERIAL_SEARCH_TYPES.sq)
    const [materialSearchQuery, setMaterialSearchQuery] = useState(
        getMaterialSearchDefaultQuery(MATERIAL_SEARCH_TYPES.sq)
    )
    const [materialSearchLoading, setMaterialSearchLoading] = useState(false)
    const [materialSearchResults, setMaterialSearchResults] = useState([])
    const [selectedMaterialSearchKeys, setSelectedMaterialSearchKeys] = useState(new Set())
    const [materialSearchResultsOpen, setMaterialSearchResultsOpen] = useState(false)
    const materialSearchRequestIdRef = useRef(0)
    const materialSearchDebounceRef = useRef(null)

    const [ownerAttachments, setOwnerAttachments] = useState([])
    const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState([])
    const [isUploadingAttachments, setIsUploadingAttachments] = useState(false)
    const attachmentInputRef = useRef(null)
    const [unitOptions, setUnitOptions] = useState([])
    const [isLoadingUnits, setIsLoadingUnits] = useState(false)
    const [materialCatalog, setMaterialCatalog] = useState([])
    const [isMaterialsCatalogLoading, setIsMaterialsCatalogLoading] = useState(false)
    const materialCatalogLoadedRef = useRef(false)
    const [materialRowSearchTerms, setMaterialRowSearchTerms] = useState({})
    const [showMaterialDropdown, setShowMaterialDropdown] = useState({})
    const [materialDropdownPos, setMaterialDropdownPos] = useState({})
    const [selectedMaterialIndex, setSelectedMaterialIndex] = useState({})
    const materialInputRefs = useRef({})
    const [showMaterialDescDropdown, setShowMaterialDescDropdown] = useState({})
    const [materialDescDropdownPos, setMaterialDescDropdownPos] = useState({})
    const [selectedMaterialDescIndex, setSelectedMaterialDescIndex] = useState({})
    const materialDescInputRefs = useRef({})

    const isOrderActive =
        String(recordStatus || '').toLowerCase() === FINAL_STATUS.toLowerCase()
    const canAccessPreview = isOrderActive || showPreviewStep

    const filledMaterials = useMemo(
        () => materials.filter((m) => !isEmptyMaterialRow(m)),
        [materials]
    )

    const materialGrossTotals = useMemo(
        () => calcGrossTotalsFromRows(filledMaterials),
        [filledMaterials]
    )

    const isStandalone = useMemo(() => {
        if (filledMaterials.length === 0) return true
        return !filledMaterials.some((m) => String(m.referenceNumber || '').trim())
    }, [filledMaterials])

    const getUserId = () => {
        const userId = localStorage.getItem('id')
        if (!userId) {
            toast.error('User session expired. Please login again.', { autoClose: 2000 })
            setTimeout(() => navigate('/'), 2000)
            return null
        }
        return userId
    }

    const applyOrderFromApi = useCallback((order) => {
        if (!order || typeof order !== 'object') return
        setSalesOrderId(order.id || order._id || editId || null)
        setRecordStatus(order.status || 'Draft')
        setCpoStatus(order.cpoStatus || order.status || 'Draft')
        setPdfLink(order.pdfLink || '')
        setPdfDownloadUrl(order.pdfDownloadUrl || '')
        setOrderDetails({
            customerName: order.customerName || '',
            customerId: String(order.customerId || '').trim(),
            customerNumber: order.customerNumber || '',
            customerMailId: order.customerMailId || '',
            cpoNumber: order.cpoNumber || '',
            cpoDate: parseDateFromApi(order.cpoDate) || getTodayIsoDate(),
            deliverDate: parseDateFromApi(order.deliverDate),
            currency: order.currency || 'INR',
            cpoSummary: order.cpoSummary || ''
        })
        setSendToEmail(String(order.customerMailId || '').trim())
        setCustomerSearch(order.customerName || '')
        selectedCustomerIdRef.current = String(order.customerId || '').trim()
        setCustomerSearchResults([])
        setShowCustomerDropdown(false)
        if (Array.isArray(order.terms) && order.terms.length > 0) {
            setTerms(
                order.terms.map((t) => ({
                    term: t.term || t.rfqTerm || t.quoteTerm || '',
                    response: t.response || t.termsResponse || t.quoteResponse || ''
                }))
            )
        } else {
            setTerms(getDefaultTerms())
        }
        if (Array.isArray(order.materials) && order.materials.length > 0) {
            setMaterials(order.materials.map(mapMaterialFromApi))
        } else {
            setMaterials([emptyMaterial()])
        }
        if (Array.isArray(order.owner) && order.owner.length > 0) {
            setOwnerAttachments(order.owner.map(mapOwnerFromApi))
        } else {
            setOwnerAttachments([])
        }
        setPendingAttachmentFiles([])
        if (String(order.status || '').toLowerCase() === FINAL_STATUS.toLowerCase()) {
            setShowPreviewStep(true)
            setActiveTab('preview')
        }
    }, [editId])

    const loadSalesOrder = useCallback(async () => {
        if (!editId) return
        setIsLoadingOrder(true)
        try {
            const response = await axios.post(API_URL, {
                action: 'get-sales-order-by-id',
                id: editId
            })
            const responseData = parseApiResponse(response.data)
            const order = responseData?.data
            if (!isApiSuccess(responseData) || !order || typeof order !== 'object') {
                toast.error(responseData?.message || 'Failed to load sales order', {
                    autoClose: 2000
                })
                navigate('/salesOrder')
                return
            }
            applyOrderFromApi(order)
        } catch (error) {
            console.error('Load sales order error:', error)
            toast.error('Failed to load sales order', { autoClose: 2000 })
            navigate('/salesOrder')
        } finally {
            setIsLoadingOrder(false)
        }
    }, [editId, navigate, applyOrderFromApi])

    const refreshSalesOrderPdf = useCallback(async (orderId) => {
        const id = orderId || salesOrderId
        if (!id) return null
        try {
            const response = await axios.post(API_URL, {
                action: 'get-sales-order-by-id',
                id,
                currentUserId: localStorage.getItem('id') || undefined
            })
            const responseData = parseApiResponse(response.data)
            const order = responseData?.data
            if (!isApiSuccess(responseData) || !order || typeof order !== 'object') {
                return null
            }
            const pdfFields = extractSoPdfFieldsFromResponse(responseData)
            if (pdfFields.pdfLink) setPdfLink(pdfFields.pdfLink)
            if (pdfFields.pdfDownloadUrl) setPdfDownloadUrl(pdfFields.pdfDownloadUrl)
            if (order.status) setRecordStatus(order.status)
            if (order.cpoStatus) setCpoStatus(order.cpoStatus)
            return { order, ...pdfFields }
        } catch (error) {
            console.error('Refresh sales order PDF error:', error)
            return null
        }
    }, [salesOrderId])

    useEffect(() => {
        const fetchUserEmail = async () => {
            const userId = localStorage.getItem('id')
            if (!userId) return
            try {
                const response = await axios.post(API_URL, {
                    action: 'get-user',
                    id: userId
                })
                const responseData = parseApiResponse(response.data)
                if (isApiSuccess(responseData) && responseData.user?.email) {
                    setUserEmail(String(responseData.user.email).trim())
                }
            } catch (error) {
                console.error('Fetch user email error:', error)
            }
        }
        fetchUserEmail()
    }, [])

    useEffect(() => {
        return () => {
            if (ccSearchDebounceRef.current) {
                clearTimeout(ccSearchDebounceRef.current)
            }
        }
    }, [])

    const getCcSearchTerm = (value) => {
        const segments = String(value || '').split(/[,;]/)
        return segments[segments.length - 1].trim()
    }

    const searchCcUsers = async (term, requestId) => {
        try {
            const response = await axios.post(API_URL, {
                action: 'get-users',
                page: 1,
                limit: 25,
                sortBy: 'createdAt',
                sortOrder: 'desc',
                search: term,
                currentUserId: localStorage.getItem('id') || undefined
            })
            if (requestId !== ccSearchRequestIdRef.current) return

            const responseData = parseApiResponse(response.data)
            const users = Array.isArray(responseData?.users)
                ? responseData.users
                : Array.isArray(responseData?.data?.users)
                  ? responseData.data.users
                  : []

            const suggestions = []
            const seen = new Set()
            users.forEach((user) => {
                const email = String(user?.email || '').trim()
                if (!isValidEmailAddress(email)) return
                const key = email.toLowerCase()
                if (seen.has(key)) return
                seen.add(key)
                suggestions.push({
                    name: String(user?.name || '').trim() || email,
                    email
                })
            })
            setCcUserSuggestions(suggestions)
        } catch (error) {
            console.error('Search CC users error:', error)
            if (requestId === ccSearchRequestIdRef.current) {
                setCcUserSuggestions([])
            }
        } finally {
            if (requestId === ccSearchRequestIdRef.current) {
                setIsSearchingCcUsers(false)
            }
        }
    }

    const handleAdditionalCcChange = (e) => {
        const value = e.target.value
        setAdditionalCcEmails(value)

        ccSearchRequestIdRef.current += 1
        const requestId = ccSearchRequestIdRef.current
        if (ccSearchDebounceRef.current) {
            clearTimeout(ccSearchDebounceRef.current)
        }

        const term = getCcSearchTerm(value)
        if (term.length < 3) {
            setCcUserSuggestions([])
            setIsSearchingCcUsers(false)
            return
        }

        setIsSearchingCcUsers(true)
        ccSearchDebounceRef.current = setTimeout(() => {
            searchCcUsers(term, requestId)
        }, 400)
    }

    const handleSelectCcUser = (email) => {
        const selected = String(email || '').trim()
        if (!selected) return

        setAdditionalCcEmails((prev) => {
            const value = String(prev || '')
            const lastComma = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'))
            const base = lastComma >= 0 ? `${value.slice(0, lastComma + 1)} ` : ''
            const existing = parseAdditionalCcEmails(base)
            if (existing.some((item) => item.toLowerCase() === selected.toLowerCase())) {
                return `${base.trim()} `.trimStart()
            }
            return `${base}${selected}, `
        })
        ccSearchRequestIdRef.current += 1
        setCcUserSuggestions([])
        setIsSearchingCcUsers(false)
    }

    const handleAdditionalCcBlur = () => {
        setTimeout(() => {
            setCcUserSuggestions([])
            setIsSearchingCcUsers(false)
        }, 180)
    }

    useEffect(() => {
        const mail = String(orderDetails.customerMailId || '').trim()
        if (mail) setSendToEmail(mail)
    }, [orderDetails.customerMailId])

    useEffect(() => {
        if (activeTab !== 'preview' || !pdfLink?.trim()) {
            setPdfPreviewUrl('')
            return undefined
        }

        let cancelled = false
        setIsLoadingPdfPreview(true)
        resolveSoPdfDownloadUrl({
            pdfDownloadUrl,
            pdfLink,
            salesOrderId,
            currentUserId: localStorage.getItem('id') || undefined
        })
            .then((url) => {
                if (cancelled) return
                setPdfPreviewUrl(url)
                if (url && url !== pdfDownloadUrl) {
                    setPdfDownloadUrl(url)
                }
            })
            .catch((error) => {
                console.error('Load sales order PDF preview error:', error)
                if (!cancelled) {
                    setPdfPreviewUrl('')
                    toast.error(error.message || 'Failed to load PDF preview', {
                        autoClose: 2000
                    })
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoadingPdfPreview(false)
            })

        return () => {
            cancelled = true
        }
    }, [activeTab, pdfLink, pdfDownloadUrl, salesOrderId])

    const searchCustomers = useCallback(async (searchTerm, options = {}) => {
        const { showToast = false } = options
        const search = String(searchTerm ?? '').trim()

        if (!search || search.length < CUSTOMER_SEARCH_MIN_LENGTH) {
            setCustomerSearchResults([])
            return
        }

        const requestId = ++customerSearchRequestIdRef.current
        setCustomerSearchLoading(true)
        try {
            const response = await axios.post(API_URL, {
                action: 'get-all-customers',
                search,
                currentUserId: localStorage.getItem('id') || undefined
            })
            if (requestId !== customerSearchRequestIdRef.current) return

            const responseData = parseApiResponse(response.data)
            const list = extractCustomersList(responseData)
            if (isApiSuccess(responseData) && Array.isArray(list) && list.length > 0) {
                const contactRows = flattenCustomersToContactRows(list)
                setCustomerSearchResults(contactRows)
                setShowCustomerDropdown(true)
                if (showToast && contactRows.length === 0) {
                    toast.info('No customer contacts found', { autoClose: 2000 })
                }
            } else {
                setCustomerSearchResults([])
                setShowCustomerDropdown(true)
                if (showToast) {
                    toast.info(responseData?.message || 'No customers found', {
                        autoClose: 2000
                    })
                }
            }
        } catch (error) {
            if (requestId !== customerSearchRequestIdRef.current) return
            console.error('Customer search error:', error)
            setCustomerSearchResults([])
            toast.error('Failed to search customers', { autoClose: 2000 })
        } finally {
            if (requestId === customerSearchRequestIdRef.current) {
                setCustomerSearchLoading(false)
            }
        }
    }, [])

    useEffect(() => {
        return () => {
            if (customerSearchDebounceRef.current) {
                clearTimeout(customerSearchDebounceRef.current)
            }
            if (materialSearchDebounceRef.current) {
                clearTimeout(materialSearchDebounceRef.current)
            }
        }
    }, [])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                customerSearchRef.current &&
                !customerSearchRef.current.contains(event.target)
            ) {
                setShowCustomerDropdown(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const fetchUnits = useCallback(async () => {
        setIsLoadingUnits(true)
        try {
            const response = await axios.post(API_URL, {
                action: 'get-all-units',
                sortBy: 'unit',
                sortOrder: 'asc',
                currentUserId: localStorage.getItem('id') || undefined
            })
            const responseData = parseApiResponse(response.data)
            const list = Array.isArray(responseData?.data?.units)
                ? responseData.data.units
                : []
            const activeUnits = list
                .map((item) => ({
                    id: item?._id || item?.id || '',
                    unit: String(item?.unit || '').trim(),
                    status: String(item?.status || '').trim()
                }))
                .filter((item) => item.unit)
                .filter(
                    (item) => !item.status || item.status.toLowerCase() === 'active'
                )

            setUnitOptions(isApiSuccess(responseData) ? activeUnits : [])
        } catch (error) {
            console.error('Fetch units error:', error)
            setUnitOptions([])
        } finally {
            setIsLoadingUnits(false)
        }
    }, [])

    useEffect(() => {
        fetchUnits()
    }, [fetchUnits])

    const fetchMaterialCatalog = useCallback(async ({ force = false } = {}) => {
        if (!force && materialCatalogLoadedRef.current) return
        setIsMaterialsCatalogLoading(true)
        try {
            const response = await axios.post(API_URL, {
                action: 'get-all-material',
                currentUserId: localStorage.getItem('id') || undefined
            })
            const responseData = parseApiResponse(response.data)
            if (isApiSuccess(responseData)) {
                setMaterialCatalog(
                    Array.isArray(responseData.data?.materials)
                        ? responseData.data.materials
                        : []
                )
                materialCatalogLoadedRef.current = true
            } else {
                setMaterialCatalog([])
            }
        } catch (error) {
            console.error('Fetch materials error:', error)
            setMaterialCatalog([])
        } finally {
            setIsMaterialsCatalogLoading(false)
        }
    }, [])

    useEffect(() => {
        if (activeTab === 'materials') {
            fetchMaterialCatalog()
        }
    }, [activeTab, fetchMaterialCatalog])

    useEffect(() => {
        setMaterialRowSearchTerms((prev) => {
            const next = { ...prev }
            materials.forEach((row, index) => {
                next[index] = row.materialCode || ''
            })
            return next
        })
    }, [materials])

    useEffect(() => {
        if (editId) {
            loadSalesOrder()
        }
    }, [editId, loadSalesOrder])

    const handleDetailsChange = (e) => {
        const { name, value } = e.target
        setOrderDetails((prev) => ({ ...prev, [name]: value }))
    }

    const clearSelectedCustomer = () => {
        selectedCustomerIdRef.current = ''
        setOrderDetails((prev) => ({
            ...prev,
            customerId: '',
            customerName: '',
            customerNumber: '',
            customerMailId: ''
        }))
    }

    const handleCustomerSearchChange = (e) => {
        const value = e.target.value
        setCustomerSearch(value)
        customerSearchRequestIdRef.current += 1

        if (customerSearchDebounceRef.current) {
            clearTimeout(customerSearchDebounceRef.current)
        }

        // Ignore programmatic value updates after picking a contact
        if (skipCustomerSearchClearRef.current) {
            skipCustomerSearchClearRef.current = false
            return
        }

        const selectedId =
            selectedCustomerIdRef.current || String(orderDetails.customerId || '').trim()
        const selectedName = String(orderDetails.customerName || '').trim()
        const typed = String(value || '').trim()

        // Only clear when user changes away from the selected customer
        if (selectedId && typed !== selectedName) {
            clearSelectedCustomer()
        }

        if (typed.length < CUSTOMER_SEARCH_MIN_LENGTH) {
            setCustomerSearchResults([])
            setShowCustomerDropdown(false)
            setCustomerSearchLoading(false)
            return
        }

        setCustomerSearchLoading(true)
        setShowCustomerDropdown(true)
        customerSearchDebounceRef.current = setTimeout(() => {
            searchCustomers(typed)
        }, CUSTOMER_SEARCH_DEBOUNCE_MS)
    }

    const selectCustomerContact = (contactRow) => {
        const customerId = normalizeRecordId(contactRow?.customerId)
        const customerName = String(contactRow?.customerName || '').trim()
        const customerMailId = String(contactRow?.email || '').trim()
        const customerNumber = String(contactRow?.contactNumber || '').trim()
        const contactPerson = String(contactRow?.contactPerson || '').trim()

        if (!customerId || !customerName) {
            toast.error('Invalid customer contact', { autoClose: 2000 })
            return
        }
        if (!customerMailId) {
            toast.error('Selected contact has no email', { autoClose: 2000 })
            return
        }

        selectedCustomerIdRef.current = customerId
        skipCustomerSearchClearRef.current = true
        setOrderDetails((prev) => ({
            ...prev,
            customerId,
            customerName,
            customerNumber,
            customerMailId
        }))
        setCustomerSearch(customerName)
        setCustomerSearchResults([])
        setShowCustomerDropdown(false)
        customerSearchRequestIdRef.current += 1
        setCustomerSearchLoading(false)
        toast.success(
            contactPerson
                ? `Selected contact: ${contactPerson}`
                : 'Customer selected',
            { autoClose: 1500 }
        )
    }

    const updateTerm = (index, field, value) => {
        setTerms((prev) =>
            prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
        )
    }

    const addTermRowAfter = (index) => {
        setTerms((prev) => {
            const next = [...prev]
            next.splice(index + 1, 0, emptyTerm())
            return next
        })
    }

    const removeTermRow = (index) => {
        setTerms((prev) => {
            if (prev.length <= 1) return prev
            return prev.filter((_, i) => i !== index)
        })
    }

    const updateMaterial = (index, field, value) => {
        setMaterials((prev) =>
            prev.map((row, i) => {
                if (i !== index) return row
                const next = { ...row, [field]: value }
                if (field === 'quantity' || field === 'cpoBasicPrice' || field === 'gstPercent') {
                    return syncMaterialPricing(next)
                }
                return next
            })
        )
    }

    const addMaterialRowAfter = (index) => {
        setMaterials((prev) => {
            const next = [...prev]
            next.splice(index + 1, 0, emptyMaterial())
            return next
        })
    }

    const removeMaterialRow = (index) => {
        setMaterials((prev) => {
            if (prev.length <= 1) return [emptyMaterial()]
            return prev.filter((_, i) => i !== index)
        })
    }

    const getFilteredMaterialCatalog = (rowIndex) => {
        const search = String(materialRowSearchTerms[rowIndex] || '').toLowerCase()
        if (!search) return materialCatalog.slice(0, 50)
        return materialCatalog.filter(
            (m) =>
                String(m.materialCode || '')
                    .toLowerCase()
                    .includes(search) ||
                String(m.productName || '')
                    .toLowerCase()
                    .includes(search) ||
                String(m.materialDescription || '')
                    .toLowerCase()
                    .includes(search)
        )
    }

    const getFilteredMaterialDescCatalog = (rowIndex) => {
        const search = String(materials[rowIndex]?.materialDescription || '').toLowerCase()
        if (!search) return materialCatalog.slice(0, 50)
        return materialCatalog.filter(
            (m) =>
                String(m.productName || '')
                    .toLowerCase()
                    .includes(search) ||
                String(m.materialDescription || '')
                    .toLowerCase()
                    .includes(search) ||
                String(m.materialCode || '')
                    .toLowerCase()
                    .includes(search) ||
                String(m.categoryName || '')
                    .toLowerCase()
                    .includes(search) ||
                String(m.subcategoryName || '')
                    .toLowerCase()
                    .includes(search)
        )
    }

    const openMaterialDropdown = useCallback(
        (rowIndex) => {
            if (isMaterialsCatalogLoading) return
            const el = materialInputRefs.current[rowIndex]
            if (el) {
                const rect = el.getBoundingClientRect()
                setMaterialDropdownPos((prev) => ({
                    ...prev,
                    [rowIndex]: { top: rect.bottom, left: rect.left, width: rect.width }
                }))
            }
            setShowMaterialDropdown((prev) => ({ ...prev, [rowIndex]: true }))
        },
        [isMaterialsCatalogLoading]
    )

    const closeMaterialDropdown = (rowIndex) => {
        setShowMaterialDropdown((prev) => ({ ...prev, [rowIndex]: false }))
        setSelectedMaterialIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
    }

    const openMaterialDescDropdown = useCallback(
        (rowIndex) => {
            if (isMaterialsCatalogLoading) return
            const el = materialDescInputRefs.current[rowIndex]
            if (el) {
                const rect = el.getBoundingClientRect()
                setMaterialDescDropdownPos((prev) => ({
                    ...prev,
                    [rowIndex]: { top: rect.bottom, left: rect.left, width: Math.max(rect.width, 280) }
                }))
            }
            setShowMaterialDescDropdown((prev) => ({ ...prev, [rowIndex]: true }))
        },
        [isMaterialsCatalogLoading]
    )

    const closeMaterialDescDropdown = (rowIndex) => {
        setShowMaterialDescDropdown((prev) => ({ ...prev, [rowIndex]: false }))
        setSelectedMaterialDescIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
    }

    const applyCatalogMaterialToRow = (rowIndex, material) => {
        setMaterials((prev) =>
            prev.map((row, i) =>
                i === rowIndex
                    ? syncMaterialPricing({
                          ...row,
                          materialCode: material.materialCode || '',
                          materialDescription:
                              material.productName || material.materialDescription || '',
                          hsn: material.hsnCode || material.hsn || '',
                          uom: material.uom || row.uom || ''
                      })
                    : row
            )
        )
        setMaterialRowSearchTerms((prev) => ({
            ...prev,
            [rowIndex]: material.materialCode || ''
        }))
    }

    const handleCatalogMaterialCodeChange = (rowIndex, value) => {
        setMaterialRowSearchTerms((prev) => ({ ...prev, [rowIndex]: value }))
        setSelectedMaterialIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
        openMaterialDropdown(rowIndex)
        updateMaterial(rowIndex, 'materialCode', value)
    }

    const handleCatalogMaterialSelect = (rowIndex, material) => {
        applyCatalogMaterialToRow(rowIndex, material)
        closeMaterialDropdown(rowIndex)
    }

    const handleCatalogMaterialKeyDown = (rowIndex, e) => {
        if (!showMaterialDropdown[rowIndex]) return
        const list = getFilteredMaterialCatalog(rowIndex)
        const currentIndex = selectedMaterialIndex[rowIndex] ?? -1

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setSelectedMaterialIndex((prev) => ({
                ...prev,
                [rowIndex]: currentIndex < list.length - 1 ? currentIndex + 1 : currentIndex
            }))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setSelectedMaterialIndex((prev) => ({
                ...prev,
                [rowIndex]: currentIndex > 0 ? currentIndex - 1 : 0
            }))
        } else if (e.key === 'Enter') {
            if (currentIndex >= 0 && list[currentIndex]) {
                e.preventDefault()
                handleCatalogMaterialSelect(rowIndex, list[currentIndex])
            }
        } else if (e.key === 'Escape') {
            closeMaterialDropdown(rowIndex)
        }
    }

    const handleCatalogMaterialDescChange = (rowIndex, value) => {
        updateMaterial(rowIndex, 'materialDescription', value)
        setSelectedMaterialDescIndex((prev) => ({ ...prev, [rowIndex]: -1 }))
        openMaterialDescDropdown(rowIndex)
    }

    const handleCatalogMaterialDescSelect = (rowIndex, material) => {
        applyCatalogMaterialToRow(rowIndex, material)
        closeMaterialDescDropdown(rowIndex)
    }

    const handleCatalogMaterialDescKeyDown = (rowIndex, e) => {
        if (!showMaterialDescDropdown[rowIndex]) return
        const list = getFilteredMaterialDescCatalog(rowIndex)
        const currentIndex = selectedMaterialDescIndex[rowIndex] ?? -1

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setSelectedMaterialDescIndex((prev) => ({
                ...prev,
                [rowIndex]: currentIndex < list.length - 1 ? currentIndex + 1 : currentIndex
            }))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setSelectedMaterialDescIndex((prev) => ({
                ...prev,
                [rowIndex]: currentIndex > 0 ? currentIndex - 1 : 0
            }))
        } else if (e.key === 'Enter') {
            if (currentIndex >= 0 && list[currentIndex]) {
                e.preventDefault()
                handleCatalogMaterialDescSelect(rowIndex, list[currentIndex])
            }
        } else if (e.key === 'Escape') {
            closeMaterialDescDropdown(rowIndex)
        }
    }

    useEffect(() => {
        const activeRowIndex = Object.keys(showMaterialDropdown).find(
            (idx) => showMaterialDropdown[idx]
        )
        if (activeRowIndex === undefined) return undefined

        const reposition = () => openMaterialDropdown(activeRowIndex)
        window.addEventListener('scroll', reposition, true)
        window.addEventListener('resize', reposition)
        return () => {
            window.removeEventListener('scroll', reposition, true)
            window.removeEventListener('resize', reposition)
        }
    }, [showMaterialDropdown, openMaterialDropdown])

    useEffect(() => {
        const activeRowIndex = Object.keys(showMaterialDescDropdown).find(
            (idx) => showMaterialDescDropdown[idx]
        )
        if (activeRowIndex === undefined) return undefined

        const reposition = () => openMaterialDescDropdown(activeRowIndex)
        window.addEventListener('scroll', reposition, true)
        window.addEventListener('resize', reposition)
        return () => {
            window.removeEventListener('scroll', reposition, true)
            window.removeEventListener('resize', reposition)
        }
    }, [showMaterialDescDropdown, openMaterialDescDropdown])

    const getMaterialSearchKey = (item, index) => {
        if (materialSearchType === MATERIAL_SEARCH_TYPES.sq) return getSqKey(item, index)
        return getProcurementKey(item, index)
    }

    const isMaterialSearchItemAdded = (item) => {
        if (materialSearchType === MATERIAL_SEARCH_TYPES.sq) {
            return isSqInMaterials(materials, item)
        }
        return isProcurementInMaterials(materials, item)
    }

    const getMaterialSearchNotFoundMessage = (searchType) =>
        searchType === MATERIAL_SEARCH_TYPES.sq
            ? 'No sales quotes found'
            : 'No procurement records found'

    const getMaterialSearchErrorMessage = (searchType) =>
        searchType === MATERIAL_SEARCH_TYPES.sq
            ? 'Failed to search sales quotes'
            : 'Failed to search procurement by PR number'

    const searchMaterialItems = useCallback(
        async (searchTerm, options = {}) => {
            const { showToast = true, searchType = materialSearchType } = options
            const query = String(searchTerm ?? '').trim()
            const userId = localStorage.getItem('id') || undefined

            if (!query) {
                setMaterialSearchResults([])
                setMaterialSearchResultsOpen(false)
                return
            }

            if (query.length < MATERIAL_SEARCH_MIN_LENGTH) {
                setMaterialSearchResults([])
                setMaterialSearchResultsOpen(false)
                return
            }

            const requestId = ++materialSearchRequestIdRef.current
            setMaterialSearchLoading(true)

            try {
                let list = []
                if (searchType === MATERIAL_SEARCH_TYPES.sq) {
                    const response = await axios.post(API_URL, {
                        action: 'get-all-sales-quotes',
                        page: 1,
                        limit: 5000,
                        search: query,
                        currentUserId: userId
                    })
                    if (requestId !== materialSearchRequestIdRef.current) return
                    const responseData = parseApiResponse(response.data)
                    list = extractSalesQuotesList(responseData)
                    if (!isApiSuccess(responseData) && list.length === 0) {
                        setMaterialSearchResults([])
                        setMaterialSearchResultsOpen(false)
                        if (showToast) {
                            toast.error(responseData?.message || 'Failed to fetch sales quotes', {
                                autoClose: 2000
                            })
                        }
                        return
                    }
                } else {
                    const response = await axios.post(API_URL, {
                        action: 'get-all-procurements',
                        page: 1,
                        limit: 5000,
                        prNo: query,
                        currentUserId: userId
                    })
                    if (requestId !== materialSearchRequestIdRef.current) return
                    const responseData = parseApiResponse(response.data)
                    list = responseData?.data?.procurements || []
                    if (!isApiSuccess(responseData)) {
                        setMaterialSearchResults([])
                        setMaterialSearchResultsOpen(false)
                        if (showToast) {
                            toast.error(responseData?.message || 'Failed to search procurements', {
                                autoClose: 2000
                            })
                        }
                        return
                    }
                }

                if (Array.isArray(list) && list.length > 0) {
                    setMaterialSearchResults(list)
                    setMaterialSearchResultsOpen(true)
                    setSelectedMaterialSearchKeys(new Set())
                } else {
                    setMaterialSearchResults([])
                    setMaterialSearchResultsOpen(false)
                    if (showToast) {
                        toast.info(getMaterialSearchNotFoundMessage(searchType), { autoClose: 2000 })
                    }
                }
            } catch (error) {
                if (requestId !== materialSearchRequestIdRef.current) return
                console.error('Material search error:', error)
                if (showToast) {
                    toast.error(getMaterialSearchErrorMessage(searchType), { autoClose: 2000 })
                }
            } finally {
                if (requestId === materialSearchRequestIdRef.current) {
                    setMaterialSearchLoading(false)
                }
            }
        },
        [materialSearchType]
    )

    const handleMaterialSearchTypeChange = (e) => {
        const nextType = e.target.value
        const nextQuery = getMaterialSearchDefaultQuery(nextType)
        setMaterialSearchType(nextType)
        setMaterialSearchQuery(nextQuery)
        setMaterialSearchResults([])
        setSelectedMaterialSearchKeys(new Set())
        setMaterialSearchResultsOpen(false)
        materialSearchRequestIdRef.current += 1

        if (materialSearchDebounceRef.current) {
            clearTimeout(materialSearchDebounceRef.current)
        }
        if (nextQuery.trim().length >= MATERIAL_SEARCH_MIN_LENGTH) {
            materialSearchDebounceRef.current = setTimeout(() => {
                searchMaterialItems(nextQuery, { showToast: false, searchType: nextType })
            }, 300)
        }
    }

    const handleMaterialSearchChange = (e) => {
        const value = e.target.value
        setMaterialSearchQuery(value)
        if (materialSearchDebounceRef.current) {
            clearTimeout(materialSearchDebounceRef.current)
        }
        materialSearchDebounceRef.current = setTimeout(() => {
            searchMaterialItems(value, { showToast: false, searchType: materialSearchType })
        }, 400)
    }

    const handleMaterialSearchClick = () =>
        searchMaterialItems(materialSearchQuery, { searchType: materialSearchType })

    const handleMaterialSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            searchMaterialItems(materialSearchQuery, { searchType: materialSearchType })
        }
    }

    const getSelectableMaterialSearchKeys = () =>
        materialSearchResults
            .map((item, index) => ({ item, key: getMaterialSearchKey(item, index) }))
            .filter(({ item }) => !isMaterialSearchItemAdded(item))
            .map(({ key }) => key)

    const toggleMaterialSearchSelection = (key) => {
        setSelectedMaterialSearchKeys((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const toggleSelectAllMaterialSearchResults = () => {
        const selectable = getSelectableMaterialSearchKeys()
        const allSelected =
            selectable.length > 0 &&
            selectable.every((key) => selectedMaterialSearchKeys.has(key))
        setSelectedMaterialSearchKeys(allSelected ? new Set() : new Set(selectable))
    }

    const countPendingMaterialSelection = () =>
        getSelectableMaterialSearchKeys().filter((key) =>
            selectedMaterialSearchKeys.has(key)
        ).length

    const countAddedInCurrentMaterialResults = () =>
        materialSearchResults.filter((item) => isMaterialSearchItemAdded(item)).length

    const buildRowsFromSearchItem = (item) => {
        if (materialSearchType === MATERIAL_SEARCH_TYPES.sq) {
            const quoteMaterials = Array.isArray(item.materials) ? item.materials : []
            if (quoteMaterials.length > 0) {
                return quoteMaterials.map((m) => sqMaterialToRow(m, item))
            }
            return [
                syncMaterialPricing({
                    ...emptyMaterial(),
                    referenceNumber: String(item.quoteNumber || '').trim(),
                    referenceNumberId: String(item._id || item.id || '').trim(),
                    materialCode: '-',
                    hsn: '-'
                })
            ]
        }
        return [procurementToMaterialRow(item)]
    }

    const applySelectedMaterialSearchResults = () => {
        const selectedItems = materialSearchResults.filter((item, index) =>
            selectedMaterialSearchKeys.has(getMaterialSearchKey(item, index))
        )
        if (selectedItems.length === 0) return

        const rowsToAdd = []
        selectedItems.forEach((item) => {
            if (isMaterialSearchItemAdded(item)) return
            buildRowsFromSearchItem(item).forEach((row) => rowsToAdd.push(row))
        })

        if (rowsToAdd.length === 0) return

        setMaterials((prev) => {
            const keyedRows = prev.filter((row) => !isEmptyMaterialRow(row))
            return [...keyedRows, ...rowsToAdd]
        })
        setSelectedMaterialSearchKeys(new Set())
    }

    const buildTermsPayload = () =>
        terms
            .filter((t) => String(t.term || '').trim())
            .map((t) => ({
                term: String(t.term).trim(),
                response: String(t.response || '').trim()
            }))

    const buildMaterialsPayload = () =>
        filledMaterials.map((m) => {
            const pricing = calcMaterialPricing(m.cpoBasicPrice, m.quantity, m.gstPercent)
            return {
                referenceNumber: String(m.referenceNumber || '').trim() || '-',
                referenceNumberId: String(m.referenceNumberId || '').trim(),
                materialCode: String(m.materialCode || '').trim() || '-',
                materialDescription: String(m.materialDescription || '').trim(),
                hsn: String(m.hsn || '').trim() || '-',
                uom: String(m.uom || '').trim(),
                quantity: parseFloat(m.quantity) || 0,
                cpoBasicPrice: parseFloat(m.cpoBasicPrice) || 0,
                gstPercent: parseFloat(m.gstPercent) || 0,
                gstValue: pricing.gstValue,
                totalValue: pricing.totalValue,
                technicalRemarks: String(m.technicalRemarks || '').trim()
            }
        })

    const validateDetails = () => {
        const customerId =
            selectedCustomerIdRef.current || String(orderDetails.customerId || '').trim()
        if (!customerId || !String(orderDetails.customerName || '').trim()) {
            toast.error('Please select a customer contact from the search results', {
                autoClose: 2500
            })
            return false
        }
        // Keep state in sync if ref has the id but state was cleared unexpectedly
        if (!orderDetails.customerId && customerId) {
            setOrderDetails((prev) => ({ ...prev, customerId }))
        }
        if (!orderDetails.customerMailId.trim()) {
            toast.error('Customer email is required', { autoClose: 2000 })
            return false
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orderDetails.customerMailId.trim())) {
            toast.error('Enter a valid customer email', { autoClose: 2000 })
            return false
        }
        if (!orderDetails.cpoNumber.trim()) {
            toast.error('CPO number is required', { autoClose: 2000 })
            return false
        }
        if (!orderDetails.cpoDate) {
            toast.error('CPO date is required', { autoClose: 2000 })
            return false
        }
        if (!orderDetails.deliverDate) {
            toast.error('Delivery date is required', { autoClose: 2000 })
            return false
        }
        if (!orderDetails.currency.trim()) {
            toast.error('Currency is required', { autoClose: 2000 })
            return false
        }
        if (!orderDetails.cpoSummary.trim()) {
            toast.error('CPO summary is required', { autoClose: 2000 })
            return false
        }
        return true
    }

    const validateMaterials = () => {
        if (filledMaterials.length === 0) {
            toast.error('Add at least one material line', { autoClose: 2000 })
            return false
        }
        for (let i = 0; i < filledMaterials.length; i++) {
            const row = filledMaterials[i]
            const idx = materials.indexOf(row) + 1
            if (!String(row.materialDescription || '').trim()) {
                toast.error(`Row ${idx}: Material description is required`, { autoClose: 2000 })
                return false
            }
            if (row.quantity === '' || Number.isNaN(parseFloat(row.quantity))) {
                toast.error(`Row ${idx}: Valid quantity is required`, { autoClose: 2000 })
                return false
            }
            if (row.cpoBasicPrice === '' || Number.isNaN(parseFloat(row.cpoBasicPrice))) {
                toast.error(`Row ${idx}: Valid CPO basic price is required`, { autoClose: 2000 })
                return false
            }
            if (row.gstPercent === '' || Number.isNaN(parseFloat(row.gstPercent))) {
                toast.error(`Row ${idx}: Valid GST % is required`, { autoClose: 2000 })
                return false
            }
        }
        return true
    }

    const postSalesOrderRequest = async (payload) => {
        const response = await axios.post(API_URL, payload)
        const responseData = parseApiResponse(response.data)
        if (!isApiSuccess(responseData)) {
            toast.error(responseData?.message || 'Failed to save sales order', {
                autoClose: 2000
            })
            return { ok: false, responseData }
        }
        return { ok: true, responseData }
    }

    const saveTab1Details = async ({ silent = false } = {}) => {
        const userId = getUserId()
        if (!userId) return false
        if (!silent) setIsLoading(true)
        try {
            const resolvedCustomerId =
                selectedCustomerIdRef.current || String(orderDetails.customerId || '').trim()
            let payload
            if (salesOrderId) {
                payload = {
                    action: 'update-sales-order',
                    id: salesOrderId,
                    currentUserId: userId,
                    customerName: orderDetails.customerName.trim(),
                    customerId: resolvedCustomerId,
                    customerNumber: orderDetails.customerNumber.trim(),
                    customerMailId: orderDetails.customerMailId.trim(),
                    cpoNumber: orderDetails.cpoNumber.trim(),
                    cpoDate: formatDateForApi(orderDetails.cpoDate),
                    deliverDate: formatDateForApi(orderDetails.deliverDate),
                    currency: orderDetails.currency.trim(),
                    cpoSummary: orderDetails.cpoSummary.trim(),
                    terms: buildTermsPayload(),
                    status: recordStatus || 'Draft',
                    cpoStatus: cpoStatus || 'Draft'
                }
            } else {
                payload = {
                    action: 'create-sales-order',
                    currentUserId: userId,
                    customerName: orderDetails.customerName.trim(),
                    customerId: resolvedCustomerId,
                    customerNumber: orderDetails.customerNumber.trim(),
                    customerMailId: orderDetails.customerMailId.trim(),
                    cpoNumber: orderDetails.cpoNumber.trim(),
                    cpoDate: formatDateForApi(orderDetails.cpoDate),
                    deliverDate: formatDateForApi(orderDetails.deliverDate),
                    currency: orderDetails.currency.trim(),
                    cpoSummary: orderDetails.cpoSummary.trim(),
                    terms: buildTermsPayload()
                }
            }

            const { ok, responseData } = await postSalesOrderRequest(payload)
            if (!ok) return false

            const data = responseData?.data
            if (data && typeof data === 'object') {
                if (!salesOrderId) {
                    const newId = extractCreatedId(responseData)
                    if (newId) {
                        setSalesOrderId(newId)
                        navigate(`/salesOrderCreate/${newId}`, { replace: true })
                    }
                }
                if (data.status) setRecordStatus(data.status)
                if (data.cpoStatus) setCpoStatus(data.cpoStatus)
                if (data.pdfLink) setPdfLink(data.pdfLink)
                if (data.cpoNumber) {
                    setOrderDetails((prev) => ({ ...prev, cpoNumber: data.cpoNumber }))
                }
            }

            if (!silent) toast.success(responseData?.message || 'Draft saved', { autoClose: 1500 })
            return true
        } catch (error) {
            console.error('Save sales order details error:', error)
            const errData = error.response?.data ? parseApiResponse(error.response.data) : null
            if (!silent) {
                toast.error(errData?.message || 'An error occurred while saving details', {
                    autoClose: 2000
                })
            }
            return false
        } finally {
            if (!silent) setIsLoading(false)
        }
    }

    const saveTab2Materials = async ({ silent = false } = {}) => {
        const userId = getUserId()
        if (!userId || !salesOrderId) return false
        if (!silent) setIsLoading(true)
        try {
            const gross = calcGrossTotalsFromRows(filledMaterials)
            const { ok, responseData } = await postSalesOrderRequest({
                action: 'update-sales-order',
                id: salesOrderId,
                currentUserId: userId,
                standalone: isStandalone ? 'yes' : 'no',
                materials: buildMaterialsPayload(),
                grossTotalBasicPrice: gross.grossTotalBasicPrice,
                grossGstValue: gross.grossGstValue,
                grossTotalValue: gross.grossTotalValue,
                status: recordStatus || 'Draft',
                cpoStatus: cpoStatus || 'Draft'
            })
            if (!ok) return false
            const data = responseData?.data
            if (data && typeof data === 'object') {
                if (Array.isArray(data.materials)) {
                    setMaterials(
                        data.materials.length > 0
                            ? data.materials.map(mapMaterialFromApi)
                            : [emptyMaterial()]
                    )
                }
            }
            if (!silent) toast.success(responseData?.message || 'Materials saved', { autoClose: 1500 })
            return true
        } catch (error) {
            console.error('Save materials error:', error)
            const errData = error.response?.data ? parseApiResponse(error.response.data) : null
            if (!silent) {
                toast.error(errData?.message || 'An error occurred while saving materials', {
                    autoClose: 2000
                })
            }
            return false
        } finally {
            if (!silent) setIsLoading(false)
        }
    }

    const persistPendingAttachments = async () => {
        if (pendingAttachmentFiles.length === 0) return true
        const userId = getUserId()
        if (!userId || !salesOrderId) return false

        const cpoNumber = orderDetails.cpoNumber.trim()
        if (!cpoNumber) {
            toast.error('CPO number is required for attachments', { autoClose: 2000 })
            return false
        }

        setIsUploadingAttachments(true)
        try {
            const files = pendingAttachmentFiles
            const contentTypesForPresign = files.map((f) => resolveFileContentType(f))

            const response = await axios.post(API_URL, {
                action: 'get-so-owner-attachment-upload-presigned-url',
                cpoNumber,
                fileNames: files.map((f) => f.name),
                contentTypes: contentTypesForPresign,
                currentUserId: userId
            })
            const responseData = parseApiResponse(response.data)

            if (!isApiSuccess(responseData) || !Array.isArray(responseData.files)) {
                toast.error(responseData?.message || 'Failed to get upload URLs', {
                    autoClose: 2000
                })
                return false
            }

            const presignedFiles = responseData.files
            if (presignedFiles.length !== files.length) {
                toast.error('Upload URL count mismatch', { autoClose: 2000 })
                return false
            }

            for (let i = 0; i < files.length; i++) {
                const meta = presignedFiles[i]
                const file = files[i]
                const contentType =
                    meta.contentType || contentTypesForPresign[i] || 'application/octet-stream'
                await putFileToPresignedUrl(meta.uploadUrl, file, contentType)
            }

            const newOwnerEntries = presignedFiles.map((f, idx) => ({
                s3Key: f.s3Key,
                url: f.url || '',
                originalFileName: f.originalFileName || f.fileName || files[idx].name,
                contentType:
                    f.contentType || contentTypesForPresign[idx] || 'application/octet-stream',
                uploadedAt: new Date().toISOString()
            }))

            const nextOwner = [...ownerAttachments, ...newOwnerEntries]
            const { ok, responseData: updateData } = await postSalesOrderRequest({
                action: 'update-sales-order',
                id: salesOrderId,
                currentUserId: userId,
                owner: nextOwner,
                status: recordStatus || 'Draft',
                cpoStatus: cpoStatus || 'Draft'
            })
            if (!ok) return false

            const updated = updateData?.data
            if (Array.isArray(updated?.owner)) {
                setOwnerAttachments(updated.owner.map(mapOwnerFromApi))
            } else {
                setOwnerAttachments(nextOwner.map(mapOwnerFromApi))
            }
            setPendingAttachmentFiles([])
            return true
        } catch (error) {
            console.error('Upload attachments error:', error)
            const errData = error.response?.data ? parseApiResponse(error.response.data) : null
            toast.error(errData?.message || 'Failed to upload attachments', { autoClose: 2000 })
            return false
        } finally {
            setIsUploadingAttachments(false)
        }
    }

    const saveTabAttachments = async ({ silent = false } = {}) => {
        const userId = getUserId()
        if (!userId || !salesOrderId) return false
        if (!silent) setIsLoading(true)
        try {
            if (pendingAttachmentFiles.length > 0) {
                const uploaded = await persistPendingAttachments()
                if (!uploaded) return false
                if (!silent) toast.success('Attachments saved', { autoClose: 1500 })
            }
            return true
        } finally {
            if (!silent) setIsLoading(false)
        }
    }

    const handleAttachmentFileSelect = (e) => {
        const files = Array.from(e.target.files || [])
        e.target.value = ''
        if (files.length === 0) return
        const totalCount =
            ownerAttachments.length + pendingAttachmentFiles.length + files.length
        if (totalCount > MAX_SO_OWNER_ATTACHMENTS) {
            toast.error(`Maximum ${MAX_SO_OWNER_ATTACHMENTS} attachments allowed`, {
                autoClose: 2000
            })
            return
        }
        setPendingAttachmentFiles((prev) => [...prev, ...files])
    }

    const removePendingAttachment = (index) => {
        setPendingAttachmentFiles((prev) => prev.filter((_, i) => i !== index))
    }

    const removeOwnerAttachment = async (s3Key) => {
        if (!s3Key || !salesOrderId) return
        const userId = getUserId()
        if (!userId) return
        setIsUploadingAttachments(true)
        try {
            const remaining = ownerAttachments.filter((a) => a.s3Key !== s3Key)
            const { ok, responseData } = await postSalesOrderRequest({
                action: 'update-sales-order',
                id: salesOrderId,
                currentUserId: userId,
                owner: remaining,
                status: recordStatus || 'Draft',
                cpoStatus: cpoStatus || 'Draft'
            })
            if (!ok) return
            if (Array.isArray(responseData?.data?.owner)) {
                setOwnerAttachments(responseData.data.owner.map(mapOwnerFromApi))
            } else {
                setOwnerAttachments(remaining)
            }
        } catch (error) {
            console.error('Remove attachment error:', error)
            toast.error('Failed to remove attachment', { autoClose: 2000 })
        } finally {
            setIsUploadingAttachments(false)
        }
    }

    const downloadOwnerAttachment = async (att) => {
        if (!att?.s3Key) return
        const userId = getUserId()
        if (!userId) return
        try {
            const response = await axios.post(API_URL, {
                action: 'get-so-owner-attachment-download-presigned-url',
                s3Keys: [att.s3Key],
                currentUserId: userId
            })
            const responseData = parseApiResponse(response.data)
            const files = responseData?.files || responseData?.data?.files || []
            const url =
                files[0]?.downloadUrl ||
                files[0]?.url ||
                files[0]?.presignedUrl ||
                att.url
            if (!isApiSuccess(responseData) || !url) {
                toast.error(responseData?.message || 'Failed to get download URL', {
                    autoClose: 2000
                })
                return
            }
            window.open(url, '_blank', 'noopener,noreferrer')
        } catch (error) {
            console.error('Download attachment error:', error)
            toast.error('Failed to download attachment', { autoClose: 2000 })
        }
    }

    const activateSalesOrder = async () => {
        const userId = getUserId()
        if (!userId || !salesOrderId) return false
        if (!validateDetails() || !validateMaterials()) return false

        setIsLoading(true)
        try {
            if (pendingAttachmentFiles.length > 0) {
                const uploaded = await persistPendingAttachments()
                if (!uploaded) return false
            }

            const gross = calcGrossTotalsFromRows(filledMaterials)
            const { ok, responseData } = await postSalesOrderRequest({
                action: 'update-sales-order',
                id: salesOrderId,
                currentUserId: userId,
                standalone: isStandalone ? 'yes' : 'no',
                materials: buildMaterialsPayload(),
                owner: ownerAttachments,
                pdfLink: pdfLink || '',
                grossTotalBasicPrice: gross.grossTotalBasicPrice,
                grossGstValue: gross.grossGstValue,
                grossTotalValue: gross.grossTotalValue,
                status: FINAL_STATUS,
                cpoStatus: FINAL_STATUS
            })
            if (!ok) return false

            const data = responseData?.data
            setRecordStatus(data?.status || FINAL_STATUS)
            setCpoStatus(data?.cpoStatus || FINAL_STATUS)

            const pdfFields = extractSoPdfFieldsFromResponse(responseData)
            if (pdfFields.pdfLink) setPdfLink(pdfFields.pdfLink)
            if (pdfFields.pdfDownloadUrl) setPdfDownloadUrl(pdfFields.pdfDownloadUrl)

            // PDF may be generated async after activate — refresh a few times
            if (!pdfFields.pdfLink) {
                for (let attempt = 0; attempt < 4; attempt++) {
                    await new Promise((resolve) => setTimeout(resolve, 1200))
                    const refreshed = await refreshSalesOrderPdf(salesOrderId)
                    if (refreshed?.pdfLink) break
                }
            }

            setShowPreviewStep(true)
            setActiveTab('preview')
            toast.success(responseData?.message || 'Sales order activated', { autoClose: 2000 })
            return true
        } catch (error) {
            console.error('Activate sales order error:', error)
            const errData = error.response?.data ? parseApiResponse(error.response.data) : null
            toast.error(errData?.message || 'Failed to activate sales order', { autoClose: 2000 })
            return false
        } finally {
            setIsLoading(false)
        }
    }

    const handleSendSalesOrderEmail = async () => {
        const userId = getUserId()
        if (!userId) return

        const orderId = salesOrderId
        if (!orderId) {
            toast.error('Sales order ID is missing', { autoClose: 2000 })
            return
        }

        const toEmail = String(sendToEmail || orderDetails.customerMailId || '').trim()
        if (!toEmail) {
            toast.error('Customer email is required', { autoClose: 2000 })
            return
        }
        if (!isValidEmailAddress(toEmail)) {
            toast.error('Enter a valid customer email', { autoClose: 2000 })
            return
        }

        if (String(recordStatus || '').toLowerCase() !== 'active') {
            toast.error('Sales order must be Active before sending email', {
                autoClose: 2000
            })
            return
        }

        if (!String(pdfLink || '').trim()) {
            toast.error('PDF is not available for this sales order', { autoClose: 2000 })
            return
        }

        const ccList = buildCcEmails(userEmail, additionalCcEmails, toEmail)
        const invalidCc = ccList.find((email) => !isValidEmailAddress(email))
        if (invalidCc) {
            toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 })
            return
        }

        // Extras only — backend always CCs the current user from userid
        const extraCcList = parseAdditionalCcEmails(additionalCcEmails).filter((email) => {
            const key = email.toLowerCase()
            return (
                key !== toEmail.toLowerCase() &&
                key !== String(userEmail || '').trim().toLowerCase()
            )
        })

        const isEditMode = Boolean(editId)
        setIsSendingEmail(true)
        try {
            const payload = isEditMode
                ? {
                      action: 'resend-sales-order-email',
                      id: orderId,
                      userid: userId,
                      ccEmails: extraCcList
                  }
                : {
                      action: 'send-sales-order-email',
                      id: orderId,
                      userid: userId,
                      toEmail,
                      ...(extraCcList.length > 0
                          ? { ccEmail: extraCcList.join(', ') }
                          : {})
                  }

            const response = await axios.post(API_URL, payload)
            const responseData = parseApiResponse(response.data)

            if (isApiSuccess(responseData)) {
                toast.success(
                    responseData?.message ||
                        (isEditMode
                            ? `Email resent to ${toEmail}`
                            : `Email sent to ${toEmail}`),
                    { autoClose: 2000 }
                )
            } else {
                toast.error(
                    responseData?.message ||
                        (isEditMode ? 'Failed to resend email' : 'Failed to send email'),
                    { autoClose: 2000 }
                )
            }
        } catch (error) {
            console.error(
                isEditMode
                    ? 'Resend sales order email error:'
                    : 'Send sales order email error:',
                error
            )
            const errData = error.response?.data
                ? parseApiResponse(error.response.data)
                : null
            toast.error(
                errData?.message ||
                    (isEditMode
                        ? 'An error occurred while resending email'
                        : 'An error occurred while sending email'),
                { autoClose: 2000 }
            )
        } finally {
            setIsSendingEmail(false)
        }
    }

    const goToTab = async (nextTab) => {
        if (nextTab === activeTab) return
        if (nextTab === 'preview' && !canAccessPreview) return

        const forward = isForwardMove(activeTab, nextTab)

        if (activeTab === 'details') {
            if (forward) {
                if (!validateDetails()) return
                if (!(await saveTab1Details())) return
            } else {
                await saveTab1Details({ silent: true })
            }
        } else if (activeTab === 'materials') {
            if (forward) {
                if (!validateMaterials()) return
                if (!(await saveTab2Materials())) return
            } else if (salesOrderId) {
                await saveTab2Materials({ silent: true })
            }
        } else if (activeTab === 'attachments') {
            if (salesOrderId) await saveTabAttachments({ silent: true })
        }

        setActiveTab(nextTab)
    }

    const handleBack = async (e) => {
        e.preventDefault()
        if (activeTab === 'details') {
            if (orderDetails.customerId && orderDetails.cpoNumber.trim()) {
                await saveTab1Details({ silent: true })
            }
        } else if (activeTab === 'materials' && salesOrderId) {
            await saveTab2Materials({ silent: true })
        } else if (activeTab === 'attachments' && salesOrderId) {
            await saveTabAttachments({ silent: true })
        }
        navigate('/salesOrder')
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (activeTab === 'attachments') {
            await activateSalesOrder()
        }
    }

    const tabClass = (tab) => (activeTab === tab ? 'nav-link active' : 'nav-link')

    return (
        <div>
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">
                                {editId ? 'Edit Sales Order' : 'Create Sales Order'}
                            </h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/salesOrder" onClick={handleBack}>
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card card-primary card-outline card-tabs">
                                <div className="card-header p-0 pt-1 border-bottom-0">
                                    <ul className="nav nav-tabs" role="tablist">
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={tabClass('details')}
                                                onClick={() => goToTab('details')}
                                            >
                                                CPO Details
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={tabClass('materials')}
                                                onClick={() => goToTab('materials')}
                                            >
                                                Materials
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={tabClass('attachments')}
                                                onClick={() => goToTab('attachments')}
                                            >
                                                Attachments
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={tabClass('preview')}
                                                onClick={() => canAccessPreview && goToTab('preview')}
                                                disabled={!canAccessPreview}
                                                style={
                                                    !canAccessPreview
                                                        ? { opacity: 0.6, cursor: 'not-allowed' }
                                                        : undefined
                                                }
                                            >
                                                Preview
                                            </button>
                                        </li>
                                    </ul>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    {isLoadingOrder ? (
                                        <div className="card-body text-center py-5">
                                            <PulseLoader color="#1199B2" size={10} />
                                        </div>
                                    ) : (
                                        <div className="card-body">
                                            {activeTab === 'details' && (
                                                <div className="row">
                                                    <div className="col-md-3">
                                                        <div className="form-group">
                                                            <label>CPO Number</label>
                                                            <input
                                                                type="text"
                                                                name="cpoNumber"
                                                                className="form-control"
                                                                value={orderDetails.cpoNumber}
                                                                onChange={handleDetailsChange}
                                                                placeholder="CPO-ITS-1001"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group">
                                                            <label>CPO Date</label>
                                                            <input
                                                                type="date"
                                                                name="cpoDate"
                                                                className="form-control"
                                                                value={orderDetails.cpoDate}
                                                                onChange={handleDetailsChange}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group">
                                                            <label>Delivery Date</label>
                                                            <input
                                                                type="date"
                                                                name="deliverDate"
                                                                className="form-control"
                                                                value={orderDetails.deliverDate}
                                                                onChange={handleDetailsChange}
                                                                min={orderDetails.cpoDate || undefined}
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group">
                                                            <label>Currency</label>
                                                            <select
                                                                name="currency"
                                                                className="form-control"
                                                                value={orderDetails.currency}
                                                                onChange={handleDetailsChange}
                                                            >
                                                                {CURRENCY_OPTIONS.map((c) => (
                                                                    <option key={c} value={c}>
                                                                        {c}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div className="col-md-4" ref={customerSearchRef}>
                                                        <div className="form-group">
                                                            <label>Customer Name</label>
                                                            <div style={{ position: 'relative' }}>
                                                                <div className="input-group">
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        value={customerSearch}
                                                                        onChange={handleCustomerSearchChange}
                                                                        onFocus={() => {
                                                                            if (customerSearchResults.length > 0) {
                                                                                setShowCustomerDropdown(true)
                                                                            }
                                                                        }}
                                                                        placeholder="Search customer name..."
                                                                        disabled={isLoading}
                                                                        autoComplete="off"
                                                                        required={!orderDetails.customerId}
                                                                    />
                                                                    <div className="input-group-append">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-blue"
                                                                            disabled={
                                                                                isLoading ||
                                                                                customerSearchLoading ||
                                                                                String(customerSearch || '').trim()
                                                                                    .length < CUSTOMER_SEARCH_MIN_LENGTH
                                                                            }
                                                                            onClick={() =>
                                                                                searchCustomers(customerSearch, {
                                                                                    showToast: true
                                                                                })
                                                                            }
                                                                        >
                                                                            {customerSearchLoading ? (
                                                                                <PulseLoader color="#fff" size={6} />
                                                                            ) : (
                                                                                <i className="fa-solid fa-search" />
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {showCustomerDropdown && (
                                                                    <div
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: '100%',
                                                                            left: 0,
                                                                            right: 0,
                                                                            zIndex: 1050,
                                                                            background: '#fff',
                                                                            border: '1px solid #ced4da',
                                                                            borderRadius: '4px',
                                                                            marginTop: '2px',
                                                                            maxHeight: '260px',
                                                                            overflowY: 'auto',
                                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                                                                        }}
                                                                    >
                                                                        {customerSearchLoading ? (
                                                                            <div
                                                                                className="text-center text-muted py-3"
                                                                                style={{ fontSize: '13px' }}
                                                                            >
                                                                                Searching...
                                                                            </div>
                                                                        ) : customerSearchResults.length === 0 ? (
                                                                            <div
                                                                                className="text-center text-muted py-3"
                                                                                style={{ fontSize: '13px' }}
                                                                            >
                                                                                No contacts found. Choose one contact per customer.
                                                                            </div>
                                                                        ) : (
                                                                            customerSearchResults.map((row) => (
                                                                                <button
                                                                                    key={row.key}
                                                                                    type="button"
                                                                                    className="btn btn-light btn-block text-left"
                                                                                    style={{
                                                                                        borderRadius: 0,
                                                                                        borderBottom: '1px solid #eee',
                                                                                        whiteSpace: 'normal',
                                                                                        padding: '8px 12px'
                                                                                    }}
                                                                                    onMouseDown={(e) => e.preventDefault()}
                                                                                    onClick={() => selectCustomerContact(row)}
                                                                                >
                                                                                    <div
                                                                                        style={{
                                                                                            fontWeight: 600,
                                                                                            fontSize: '13px',
                                                                                            color: '#003366'
                                                                                        }}
                                                                                    >
                                                                                        {row.customerName || '-'}
                                                                                    </div>
                                                                                    <div
                                                                                        className="text-muted"
                                                                                        style={{ fontSize: '12px' }}
                                                                                    >
                                                                                        {row.contactPerson || 'No contact person'}
                                                                                        {row.designation
                                                                                            ? ` · ${row.designation}`
                                                                                            : ''}
                                                                                    </div>
                                                                                    <div
                                                                                        className="text-muted"
                                                                                        style={{ fontSize: '12px' }}
                                                                                    >
                                                                                        {row.email || 'No email'}
                                                                                        {row.contactNumber
                                                                                            ? ` · ${row.contactNumber}`
                                                                                            : ''}
                                                                                    </div>
                                                                                </button>
                                                                            ))
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {orderDetails.customerId ? (
                                                                <small className="form-text text-success">
                                                                    Customer selected
                                                                    {orderDetails.customerMailId
                                                                        ? ` · ${orderDetails.customerMailId}`
                                                                        : ''}
                                                                </small>
                                                            ) : (
                                                                <small className="form-text text-muted">
                                                                    Search and click one contact from the list to select.
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="form-group">
                                                            <label>Customer Number</label>
                                                            <input
                                                                type="text"
                                                                name="customerNumber"
                                                                className="form-control"
                                                                value={orderDetails.customerNumber}
                                                                onChange={handleDetailsChange}
                                                                placeholder="Contact number"
                                                                readOnly
                                                                style={{ backgroundColor: '#e9ecef' }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="form-group">
                                                            <label>Customer Mail ID</label>
                                                            <input
                                                                type="email"
                                                                name="customerMailId"
                                                                className="form-control"
                                                                value={orderDetails.customerMailId}
                                                                onChange={handleDetailsChange}
                                                                placeholder="buyer@example.com"
                                                                readOnly
                                                                required
                                                                style={{ backgroundColor: '#e9ecef' }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-md-12">
                                                        <div className="form-group">
                                                            <label>CPO Summary</label>
                                                            <textarea
                                                                name="cpoSummary"
                                                                className="form-control"
                                                                rows={4}
                                                                placeholder="Summary"
                                                                value={orderDetails.cpoSummary}
                                                                onChange={handleDetailsChange}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-md-12 mt-3">
                                                        <label className="mb-2">Terms</label>
                                                        <div style={{ overflowX: 'auto' }}>
                                                            <table
                                                                className="table table-bordered table-hover mb-0"
                                                                style={{ tableLayout: 'fixed', width: '100%' }}
                                                            >
                                                                <colgroup>
                                                                    <col style={{ width: '56px' }} />
                                                                    <col style={{ width: '28%' }} />
                                                                    <col style={{ width: 'calc(72% - 146px)' }} />
                                                                    <col style={{ width: '90px' }} />
                                                                </colgroup>
                                                                <thead>
                                                                    <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                                                        <th style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                                            S.No
                                                                        </th>
                                                                        <th style={{ fontSize: '12px' }}>Term</th>
                                                                        <th style={{ fontSize: '12px' }}>Response</th>
                                                                        <th style={{ fontSize: '12px' }}>Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {terms.map((row, index) => (
                                                                        <tr key={index}>
                                                                            <td style={{ verticalAlign: 'top' }}>{index + 1}</td>
                                                                            <td style={{ verticalAlign: 'top' }}>
                                                                                <input
                                                                                    type="text"
                                                                                    className="form-control form-control-sm"
                                                                                    value={row.term}
                                                                                    onChange={(e) =>
                                                                                        updateTerm(index, 'term', e.target.value)
                                                                                    }
                                                                                    style={{ width: '100%' }}
                                                                                />
                                                                            </td>
                                                                            <td style={{ verticalAlign: 'top' }}>
                                                                                <textarea
                                                                                    className="form-control form-control-sm"
                                                                                    rows={2}
                                                                                    value={row.response}
                                                                                    onChange={(e) =>
                                                                                        updateTerm(index, 'response', e.target.value)
                                                                                    }
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        resize: 'vertical',
                                                                                        whiteSpace: 'pre-wrap',
                                                                                        wordBreak: 'break-word'
                                                                                    }}
                                                                                />
                                                                            </td>
                                                                            <td
                                                                                className="text-center"
                                                                                style={{
                                                                                    whiteSpace: 'nowrap',
                                                                                    verticalAlign: 'top'
                                                                                }}
                                                                            >
                                                                                <div className="btn-group" role="group">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-sm btn-success"
                                                                                        onClick={() => addTermRowAfter(index)}
                                                                                        style={{ padding: '3px 8px' }}
                                                                                    >
                                                                                        <i className="fa-solid fa-plus" aria-hidden="true" />
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-sm btn-danger"
                                                                                        onClick={() => removeTermRow(index)}
                                                                                        disabled={terms.length <= 1}
                                                                                        style={{ padding: '3px 8px' }}
                                                                                    >
                                                                                        <i
                                                                                            className="fa-solid fa-trash-can"
                                                                                            aria-hidden="true"
                                                                                        />
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab === 'materials' && (
                                                <div>
                                                    <div className="row align-items-end mb-3">
                                                        <div className="col-md-8">
                                                            <div className="form-group mb-0">
                                                                <label>Search by</label>
                                                                <div className="input-group">
                                                                    <div className="input-group-prepend">
                                                                        <select
                                                                            className="form-control"
                                                                            value={materialSearchType}
                                                                            onChange={handleMaterialSearchTypeChange}
                                                                            style={{ maxWidth: '130px', flex: '0 0 130px' }}
                                                                        >
                                                                            <option value={MATERIAL_SEARCH_TYPES.pr}>
                                                                                PR
                                                                            </option>
                                                                            <option value={MATERIAL_SEARCH_TYPES.sq}>
                                                                                SQ
                                                                            </option>
                                                                        </select>
                                                                    </div>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder={
                                                                            materialSearchType ===
                                                                            MATERIAL_SEARCH_TYPES.sq
                                                                                ? 'Enter SQ number...'
                                                                                : 'Enter PR number...'
                                                                        }
                                                                        value={materialSearchQuery}
                                                                        onChange={handleMaterialSearchChange}
                                                                        onKeyPress={handleMaterialSearchKeyPress}
                                                                    />
                                                                    <div className="input-group-append">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-blue"
                                                                            onClick={handleMaterialSearchClick}
                                                                            disabled={materialSearchLoading}
                                                                        >
                                                                            {materialSearchLoading ? (
                                                                                <PulseLoader color="#fff" size={6} />
                                                                            ) : (
                                                                                <i className="fa-solid fa-search" />
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4 text-md-right">
                                                            <span className="badge badge-secondary" style={{ fontSize: '12px' }}>
                                                                Mode: {isStandalone ? 'Standalone' : 'Linked'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {materialSearchResults.length > 0 && (
                                                        <div
                                                            className="mb-3"
                                                            style={{
                                                                border: '1px solid #dee2e6',
                                                                borderRadius: '6px',
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            <div
                                                                className="d-flex justify-content-between align-items-center flex-wrap"
                                                                style={{
                                                                    padding: '8px 12px',
                                                                    backgroundColor: '#f8f9fa',
                                                                    gap: '8px',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={() =>
                                                                    setMaterialSearchResultsOpen((open) => !open)
                                                                }
                                                                role="button"
                                                                tabIndex={0}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault()
                                                                        setMaterialSearchResultsOpen((open) => !open)
                                                                    }
                                                                }}
                                                            >
                                                                <span
                                                                    className="d-flex align-items-center"
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        fontSize: '13px',
                                                                        gap: '8px'
                                                                    }}
                                                                >
                                                                    <i
                                                                        className={`fa-solid ${
                                                                            materialSearchResultsOpen
                                                                                ? 'fa-chevron-up'
                                                                                : 'fa-chevron-down'
                                                                        }`}
                                                                        style={{ width: '14px', color: '#003366' }}
                                                                        aria-hidden
                                                                    />
                                                                    {materialSearchType === MATERIAL_SEARCH_TYPES.sq
                                                                        ? 'SQ search results'
                                                                        : 'PR search results'}{' '}
                                                                    — {countPendingMaterialSelection()} to add,{' '}
                                                                    {countAddedInCurrentMaterialResults()} already added
                                                                </span>
                                                                {materialSearchResultsOpen && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-blue"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            applySelectedMaterialSearchResults()
                                                                        }}
                                                                        disabled={countPendingMaterialSelection() === 0}
                                                                    >
                                                                        Add
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {materialSearchResultsOpen && (
                                                                <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                                                    {materialSearchType === MATERIAL_SEARCH_TYPES.sq ? (
                                                                        <table className="table table-sm table-bordered mb-0">
                                                                            <thead>
                                                                                <tr
                                                                                    style={{
                                                                                        backgroundColor: '#003366',
                                                                                        color: '#fff'
                                                                                    }}
                                                                                >
                                                                                    <th
                                                                                        style={{
                                                                                            fontSize: '11px',
                                                                                            width: '36px'
                                                                                        }}
                                                                                    >
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={
                                                                                                getSelectableMaterialSearchKeys()
                                                                                                    .length > 0 &&
                                                                                                getSelectableMaterialSearchKeys().every(
                                                                                                    (key) =>
                                                                                                        selectedMaterialSearchKeys.has(
                                                                                                            key
                                                                                                        )
                                                                                                )
                                                                                            }
                                                                                            onChange={
                                                                                                toggleSelectAllMaterialSearchResults
                                                                                            }
                                                                                            title="Select all not yet added"
                                                                                        />
                                                                                    </th>
                                                                                    <th style={{ fontSize: '11px' }}>
                                                                                        SQ No
                                                                                    </th>
                                                                                    <th style={{ fontSize: '11px' }}>
                                                                                        Summary
                                                                                    </th>
                                                                                    <th style={{ fontSize: '11px' }}>
                                                                                        Status
                                                                                    </th>
                                                                                    <th style={{ fontSize: '11px' }}>
                                                                                        Lines
                                                                                    </th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {materialSearchResults.map(
                                                                                    (item, idx) => {
                                                                                        const rowKey =
                                                                                            getMaterialSearchKey(
                                                                                                item,
                                                                                                idx
                                                                                            )
                                                                                        const isAdded =
                                                                                            isMaterialSearchItemAdded(
                                                                                                item
                                                                                            )
                                                                                        const isSelected =
                                                                                            selectedMaterialSearchKeys.has(
                                                                                                rowKey
                                                                                            )
                                                                                        const lineCount =
                                                                                            Array.isArray(item.materials)
                                                                                                ? item.materials.length
                                                                                                : 0
                                                                                        return (
                                                                                            <tr
                                                                                                key={rowKey}
                                                                                                style={{
                                                                                                    backgroundColor:
                                                                                                        isAdded
                                                                                                            ? '#e8f5e9'
                                                                                                            : isSelected
                                                                                                              ? '#e8f4f8'
                                                                                                              : undefined
                                                                                                }}
                                                                                            >
                                                                                                <td className="text-center">
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        checked={isSelected}
                                                                                                        disabled={isAdded}
                                                                                                        onChange={() =>
                                                                                                            toggleMaterialSearchSelection(
                                                                                                                rowKey
                                                                                                            )
                                                                                                        }
                                                                                                    />
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        fontSize: '12px'
                                                                                                    }}
                                                                                                >
                                                                                                    {item.quoteNumber ||
                                                                                                        '-'}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        fontSize: '12px'
                                                                                                    }}
                                                                                                >
                                                                                                    {item.quoteSummary ||
                                                                                                        item.summary ||
                                                                                                        '-'}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        fontSize: '12px'
                                                                                                    }}
                                                                                                >
                                                                                                    {item.status ||
                                                                                                        item.quoteStatus ||
                                                                                                        '-'}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        fontSize: '12px'
                                                                                                    }}
                                                                                                >
                                                                                                    {lineCount}
                                                                                                </td>
                                                                                            </tr>
                                                                                        )
                                                                                    }
                                                                                )}
                                                                            </tbody>
                                                                        </table>
                                                                    ) : (
                                                                        <table className="table table-sm table-bordered mb-0">
                                                                            <thead>
                                                                                <tr
                                                                                    style={{
                                                                                        backgroundColor: '#003366',
                                                                                        color: '#fff'
                                                                                    }}
                                                                                >
                                                                                    <th
                                                                                        style={{
                                                                                            fontSize: '11px',
                                                                                            width: '36px'
                                                                                        }}
                                                                                    >
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={
                                                                                                getSelectableMaterialSearchKeys()
                                                                                                    .length > 0 &&
                                                                                                getSelectableMaterialSearchKeys().every(
                                                                                                    (key) =>
                                                                                                        selectedMaterialSearchKeys.has(
                                                                                                            key
                                                                                                        )
                                                                                                )
                                                                                            }
                                                                                            onChange={
                                                                                                toggleSelectAllMaterialSearchResults
                                                                                            }
                                                                                            title="Select all not yet added"
                                                                                        />
                                                                                    </th>
                                                                                    <th style={{ fontSize: '11px' }}>
                                                                                        Ref. Number
                                                                                    </th>
                                                                                    <th style={{ fontSize: '11px' }}>
                                                                                        Material Code
                                                                                    </th>
                                                                                    <th style={{ fontSize: '11px' }}>
                                                                                        Material Description
                                                                                    </th>
                                                                                    <th style={{ fontSize: '11px' }}>
                                                                                        HSN
                                                                                    </th>
                                                                                    <th style={{ fontSize: '11px' }}>
                                                                                        UOM
                                                                                    </th>
                                                                                    <th style={{ fontSize: '11px' }}>
                                                                                        Quantity
                                                                                    </th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {materialSearchResults.map(
                                                                                    (item, idx) => {
                                                                                        const rowKey =
                                                                                            getMaterialSearchKey(
                                                                                                item,
                                                                                                idx
                                                                                            )
                                                                                        const isAdded =
                                                                                            isMaterialSearchItemAdded(
                                                                                                item
                                                                                            )
                                                                                        const isSelected =
                                                                                            selectedMaterialSearchKeys.has(
                                                                                                rowKey
                                                                                            )
                                                                                        return (
                                                                                            <tr
                                                                                                key={rowKey}
                                                                                                style={{
                                                                                                    backgroundColor:
                                                                                                        isAdded
                                                                                                            ? '#e8f5e9'
                                                                                                            : isSelected
                                                                                                              ? '#e8f4f8'
                                                                                                              : undefined
                                                                                                }}
                                                                                            >
                                                                                                <td className="text-center">
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        checked={isSelected}
                                                                                                        disabled={isAdded}
                                                                                                        onChange={() =>
                                                                                                            toggleMaterialSearchSelection(
                                                                                                                rowKey
                                                                                                            )
                                                                                                        }
                                                                                                    />
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        fontSize: '12px'
                                                                                                    }}
                                                                                                >
                                                                                                    {item.prNo ||
                                                                                                        item.prNumber ||
                                                                                                        '-'}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        fontSize: '12px'
                                                                                                    }}
                                                                                                >
                                                                                                    {item.materialCode ||
                                                                                                        '-'}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        fontSize: '12px',
                                                                                                        maxWidth: '180px',
                                                                                                        whiteSpace:
                                                                                                            'normal',
                                                                                                        wordBreak:
                                                                                                            'break-word'
                                                                                                    }}
                                                                                                >
                                                                                                    {item.materialDescription ||
                                                                                                        '-'}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        fontSize: '12px'
                                                                                                    }}
                                                                                                >
                                                                                                    {item.hsnCode ||
                                                                                                        item.hsn ||
                                                                                                        '-'}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        fontSize: '12px'
                                                                                                    }}
                                                                                                >
                                                                                                    {item.uom || '-'}
                                                                                                </td>
                                                                                                <td
                                                                                                    style={{
                                                                                                        fontSize: '12px'
                                                                                                    }}
                                                                                                >
                                                                                                    {item.quantity ?? '-'}
                                                                                                </td>
                                                                                            </tr>
                                                                                        )
                                                                                    }
                                                                                )}
                                                                            </tbody>
                                                                        </table>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    <div style={{ overflowX: 'auto' }}>
                                                        <table
                                                            className="table table-bordered table-hover mb-0"
                                                            style={{ minWidth: '1600px', tableLayout: 'fixed' }}
                                                        >
                                                            <thead>
                                                                <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                                                    <th style={{ fontSize: '12px', width: '50px' }}>S.No</th>
                                                                    <th style={{ fontSize: '12px', width: '110px' }}>Ref No</th>
                                                                    <th style={{ fontSize: '12px', minWidth: '160px', width: '160px' }}>
                                                                        Material Code
                                                                    </th>
                                                                    <th style={{ fontSize: '12px', minWidth: '280px', width: '280px' }}>
                                                                        Description
                                                                    </th>
                                                                    <th style={{ fontSize: '12px', width: '90px' }}>HSN</th>
                                                                    <th style={{ fontSize: '12px', width: '80px' }}>UOM</th>
                                                                    <th style={{ fontSize: '12px', width: '90px' }}>Qty</th>
                                                                    <th style={{ fontSize: '12px', width: '110px' }}>CPO Basic</th>
                                                                    <th style={{ fontSize: '12px', minWidth: '100px', width: '100px' }}>
                                                                        GST %
                                                                    </th>
                                                                    <th style={{ fontSize: '12px', width: '120px' }}>GST Value</th>
                                                                    <th style={{ fontSize: '12px', width: '120px' }}>Total</th>
                                                                    <th style={{ fontSize: '12px', minWidth: '200px', width: '200px' }}>
                                                                        Remarks
                                                                    </th>
                                                                    <th style={{ fontSize: '12px', width: '90px' }}>Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {materials.map((row, index) => (
                                                                    <tr key={index}>
                                                                        <td>{index + 1}</td>
                                                                        <td>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={row.referenceNumber}
                                                                                onChange={(e) =>
                                                                                    updateMaterial(index, 'referenceNumber', e.target.value)
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td style={{ minWidth: '160px' }}>
                                                                            <div
                                                                                style={{
                                                                                    position: 'relative',
                                                                                    minWidth: '140px'
                                                                                }}
                                                                            >
                                                                                <input
                                                                                    ref={(el) => {
                                                                                        materialInputRefs.current[index] = el
                                                                                    }}
                                                                                    type="text"
                                                                                    className="form-control form-control-sm"
                                                                                    placeholder={
                                                                                        isMaterialsCatalogLoading
                                                                                            ? 'Loading materials...'
                                                                                            : 'Search material code...'
                                                                                    }
                                                                                    value={
                                                                                        materialRowSearchTerms[index] ??
                                                                                        row.materialCode
                                                                                    }
                                                                                    onChange={(e) =>
                                                                                        handleCatalogMaterialCodeChange(
                                                                                            index,
                                                                                            e.target.value
                                                                                        )
                                                                                    }
                                                                                    onKeyDown={(e) =>
                                                                                        handleCatalogMaterialKeyDown(
                                                                                            index,
                                                                                            e
                                                                                        )
                                                                                    }
                                                                                    onFocus={() =>
                                                                                        openMaterialDropdown(index)
                                                                                    }
                                                                                    onBlur={() =>
                                                                                        setTimeout(
                                                                                            () =>
                                                                                                closeMaterialDropdown(
                                                                                                    index
                                                                                                ),
                                                                                            200
                                                                                        )
                                                                                    }
                                                                                    autoComplete="off"
                                                                                    disabled={isMaterialsCatalogLoading}
                                                                                    style={{ minWidth: '140px' }}
                                                                                />
                                                                                {isMaterialsCatalogLoading && (
                                                                                    <div
                                                                                        style={{
                                                                                            position: 'absolute',
                                                                                            right: '8px',
                                                                                            top: '50%',
                                                                                            transform: 'translateY(-50%)',
                                                                                            pointerEvents: 'none'
                                                                                        }}
                                                                                    >
                                                                                        <PulseLoader color="#1199B2" size={4} />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                        <td style={{ minWidth: '280px' }}>
                                                                            <textarea
                                                                                ref={(el) => {
                                                                                    materialDescInputRefs.current[index] = el
                                                                                }}
                                                                                className="form-control form-control-sm"
                                                                                rows={2}
                                                                                placeholder={
                                                                                    isMaterialsCatalogLoading
                                                                                        ? 'Loading materials...'
                                                                                        : 'Search material description...'
                                                                                }
                                                                                value={row.materialDescription}
                                                                                onChange={(e) =>
                                                                                    handleCatalogMaterialDescChange(
                                                                                        index,
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                                onKeyDown={(e) =>
                                                                                    handleCatalogMaterialDescKeyDown(
                                                                                        index,
                                                                                        e
                                                                                    )
                                                                                }
                                                                                onFocus={() =>
                                                                                    openMaterialDescDropdown(index)
                                                                                }
                                                                                onBlur={() =>
                                                                                    setTimeout(
                                                                                        () =>
                                                                                            closeMaterialDescDropdown(
                                                                                                index
                                                                                            ),
                                                                                        200
                                                                                    )
                                                                                }
                                                                                disabled={isMaterialsCatalogLoading}
                                                                                style={{
                                                                                    minWidth: '260px',
                                                                                    width: '100%',
                                                                                    resize: 'vertical',
                                                                                    whiteSpace: 'pre-wrap',
                                                                                    wordBreak: 'break-word'
                                                                                }}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={row.hsn}
                                                                                onChange={(e) =>
                                                                                    updateMaterial(index, 'hsn', e.target.value)
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <select
                                                                                className="form-control form-control-sm"
                                                                                value={row.uom || ''}
                                                                                onChange={(e) =>
                                                                                    updateMaterial(index, 'uom', e.target.value)
                                                                                }
                                                                                disabled={isLoading || isLoadingUnits}
                                                                            >
                                                                                <option value="">
                                                                                    {isLoadingUnits ? 'Loading...' : 'UOM'}
                                                                                </option>
                                                                                {row.uom &&
                                                                                    !unitOptions.some(
                                                                                        (opt) =>
                                                                                            opt.unit.toLowerCase() ===
                                                                                            String(row.uom).toLowerCase()
                                                                                    ) && (
                                                                                        <option value={row.uom}>
                                                                                            {row.uom}
                                                                                        </option>
                                                                                    )}
                                                                                {unitOptions.map((opt) => (
                                                                                    <option
                                                                                        key={opt.id || opt.unit}
                                                                                        value={opt.unit}
                                                                                    >
                                                                                        {opt.unit}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="number"
                                                                                className="form-control form-control-sm"
                                                                                value={row.quantity}
                                                                                onChange={(e) =>
                                                                                    updateMaterial(index, 'quantity', e.target.value)
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="number"
                                                                                className="form-control form-control-sm"
                                                                                value={row.cpoBasicPrice}
                                                                                onChange={(e) =>
                                                                                    updateMaterial(index, 'cpoBasicPrice', e.target.value)
                                                                                }
                                                                            />
                                                                        </td>
                                                                        <td style={{ minWidth: '100px' }}>
                                                                            <select
                                                                                className="form-control form-control-sm"
                                                                                value={normalizeGstPercent(row.gstPercent)}
                                                                                onChange={(e) =>
                                                                                    updateMaterial(index, 'gstPercent', e.target.value)
                                                                                }
                                                                                style={{ minWidth: '90px' }}
                                                                            >
                                                                                {GST_PERCENT_OPTIONS.map((gst) => (
                                                                                    <option key={gst} value={gst}>
                                                                                        {gst}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={row.gstValue}
                                                                                readOnly
                                                                                style={{ backgroundColor: '#e9ecef' }}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={row.totalValue}
                                                                                readOnly
                                                                                style={{ backgroundColor: '#e9ecef' }}
                                                                            />
                                                                        </td>
                                                                        <td style={{ minWidth: '200px' }}>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={row.technicalRemarks}
                                                                                onChange={(e) =>
                                                                                    updateMaterial(
                                                                                        index,
                                                                                        'technicalRemarks',
                                                                                        e.target.value
                                                                                    )
                                                                                }
                                                                                style={{ minWidth: '180px', width: '100%' }}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                                                            <div className="btn-group" role="group">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-success"
                                                                                    onClick={() => addMaterialRowAfter(index)}
                                                                                    style={{ padding: '3px 8px' }}
                                                                                >
                                                                                    <i className="fa-solid fa-plus" aria-hidden="true" />
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-danger"
                                                                                    onClick={() => removeMaterialRow(index)}
                                                                                    style={{ padding: '3px 8px' }}
                                                                                >
                                                                                    <i
                                                                                        className="fa-solid fa-trash-can"
                                                                                        aria-hidden="true"
                                                                                    />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot>
                                                                <tr>
                                                                    <td colSpan={9} className="text-right fwt_600">
                                                                        Gross Totals: {formatAmount(materialGrossTotals.grossTotalBasicPrice)}
                                                                    </td>
                                                                    <td className="fwt_600">
                                                                        {formatAmount(materialGrossTotals.grossGstValue)}
                                                                    </td>
                                                                    <td className="fwt_600">
                                                                        {formatAmount(materialGrossTotals.grossTotalValue)}
                                                                    </td>
                                                                    
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                        {materials.map((row, index) => {
                                                            if (!showMaterialDropdown[index]) return null
                                                            const pos = materialDropdownPos[index]
                                                            if (!pos) return null
                                                            const filtered = getFilteredMaterialCatalog(index)
                                                            return createPortal(
                                                                <div
                                                                    key={`so-material-dd-${index}`}
                                                                    style={{
                                                                        position: 'fixed',
                                                                        top: pos.top,
                                                                        left: pos.left,
                                                                        width: pos.width,
                                                                        maxHeight: '240px',
                                                                        overflowY: 'auto',
                                                                        backgroundColor: '#fff',
                                                                        border: '1px solid #ced4da',
                                                                        borderRadius: '4px',
                                                                        zIndex: 20000,
                                                                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
                                                                    }}
                                                                >
                                                                    {filtered.length > 0 ? (
                                                                        filtered.map((material, mi) => (
                                                                            <div
                                                                                key={material._id || material.materialCode}
                                                                                onMouseDown={(e) => {
                                                                                    e.preventDefault()
                                                                                    handleCatalogMaterialSelect(
                                                                                        index,
                                                                                        material
                                                                                    )
                                                                                }}
                                                                                onMouseEnter={() =>
                                                                                    setSelectedMaterialIndex((prev) => ({
                                                                                        ...prev,
                                                                                        [index]: mi
                                                                                    }))
                                                                                }
                                                                                style={{
                                                                                    padding: '8px 12px',
                                                                                    cursor: 'pointer',
                                                                                    borderBottom: '1px solid #f0f0f0',
                                                                                    backgroundColor:
                                                                                        mi ===
                                                                                        (selectedMaterialIndex[index] ?? -1)
                                                                                            ? '#e9ecef'
                                                                                            : '#fff'
                                                                                }}
                                                                            >
                                                                                <strong style={{ fontSize: '13px' }}>
                                                                                    {material.materialCode}
                                                                                </strong>
                                                                                <div
                                                                                    className="text-muted"
                                                                                    style={{ fontSize: '12px' }}
                                                                                >
                                                                                    {material.productName ||
                                                                                        material.materialDescription ||
                                                                                        '-'}
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    ) : materialRowSearchTerms[index] ? (
                                                                        <div
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                color: '#6c757d',
                                                                                fontSize: '13px'
                                                                            }}
                                                                        >
                                                                            No materials found
                                                                        </div>
                                                                    ) : null}
                                                                </div>,
                                                                document.body
                                                            )
                                                        })}
                                                        {materials.map((row, index) => {
                                                            if (!showMaterialDescDropdown[index]) return null
                                                            const pos = materialDescDropdownPos[index]
                                                            if (!pos) return null
                                                            const filtered = getFilteredMaterialDescCatalog(index)
                                                            return createPortal(
                                                                <div
                                                                    key={`so-material-desc-dd-${index}`}
                                                                    style={{
                                                                        position: 'fixed',
                                                                        top: pos.top,
                                                                        left: pos.left,
                                                                        width: pos.width,
                                                                        maxHeight: '250px',
                                                                        overflowY: 'auto',
                                                                        backgroundColor: '#fff',
                                                                        border: '1px solid #ced4da',
                                                                        borderRadius: '4px',
                                                                        zIndex: 20000,
                                                                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
                                                                    }}
                                                                >
                                                                    {filtered.length > 0 ? (
                                                                        filtered.map((material, mi) => (
                                                                            <div
                                                                                key={`${material._id || material.materialCode}-desc`}
                                                                                onMouseDown={(e) => {
                                                                                    e.preventDefault()
                                                                                    handleCatalogMaterialDescSelect(
                                                                                        index,
                                                                                        material
                                                                                    )
                                                                                }}
                                                                                onMouseEnter={() =>
                                                                                    setSelectedMaterialDescIndex(
                                                                                        (prev) => ({
                                                                                            ...prev,
                                                                                            [index]: mi
                                                                                        })
                                                                                    )
                                                                                }
                                                                                style={{
                                                                                    padding: '8px 12px',
                                                                                    cursor: 'pointer',
                                                                                    borderBottom: '1px solid #f0f0f0',
                                                                                    backgroundColor:
                                                                                        mi ===
                                                                                        (selectedMaterialDescIndex[index] ??
                                                                                            -1)
                                                                                            ? '#e9ecef'
                                                                                            : '#fff'
                                                                                }}
                                                                            >
                                                                                <strong style={{ fontSize: '13px' }}>
                                                                                    {material.productName ||
                                                                                        material.materialDescription ||
                                                                                        '-'}
                                                                                </strong>
                                                                                <div
                                                                                    className="text-muted"
                                                                                    style={{ fontSize: '12px' }}
                                                                                >
                                                                                    {material.materialCode || '-'}
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    ) : row.materialDescription ? (
                                                                        <div
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                color: '#6c757d',
                                                                                fontSize: '13px'
                                                                            }}
                                                                        >
                                                                            No materials found
                                                                        </div>
                                                                    ) : null}
                                                                </div>,
                                                                document.body
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab === 'attachments' && (
                                                <div>
                                                    <div className="row mb-3">
                                                        <div className="col-md-8">
                                                            <p className="text-muted mb-2" style={{ fontSize: '13px' }}>
                                                                Upload supporting documents for this sales order.
                                                            </p>
                                                            <input
                                                                ref={attachmentInputRef}
                                                                type="file"
                                                                multiple
                                                                style={{ display: 'none' }}
                                                                onChange={handleAttachmentFileSelect}
                                                                disabled={isLoading || isUploadingAttachments}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-blue"
                                                                onClick={() => attachmentInputRef.current?.click()}
                                                                disabled={
                                                                    isLoading ||
                                                                    isUploadingAttachments ||
                                                                    ownerAttachments.length + pendingAttachmentFiles.length >=
                                                                    MAX_SO_OWNER_ATTACHMENTS
                                                                }
                                                            >
                                                                <i className="fa-solid fa-paperclip mr-1" />
                                                                Choose Files
                                                            </button>
                                                            <span className="text-muted ml-2" style={{ fontSize: '12px' }}>
                                                                {ownerAttachments.length + pendingAttachmentFiles.length} /{' '}
                                                                {MAX_SO_OWNER_ATTACHMENTS} files
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {isUploadingAttachments && (
                                                        <div className="mb-3 text-muted" style={{ fontSize: '13px' }}>
                                                            <span className="mr-2">
                                                                <PulseLoader color="#1199B2" size={6} />
                                                            </span>
                                                            Uploading attachments...
                                                        </div>
                                                    )}

                                                    {ownerAttachments.length > 0 && (
                                                        <div className="mb-4">
                                                            <div
                                                                className="fwt_600 mb-2"
                                                                style={{ fontSize: '14px', color: '#003366' }}
                                                            >
                                                                Uploaded attachments
                                                            </div>
                                                            <div
                                                                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                                                            >
                                                                {ownerAttachments.map((att) => (
                                                                    <div
                                                                        key={att.s3Key}
                                                                        className="d-flex justify-content-between align-items-center p-2"
                                                                        style={{
                                                                            border: '1px solid #dee2e6',
                                                                            borderRadius: '6px',
                                                                            background: '#f8f9fa',
                                                                            gap: '10px'
                                                                        }}
                                                                    >
                                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                                            <div
                                                                                style={{
                                                                                    fontSize: '13px',
                                                                                    fontWeight: 600,
                                                                                    wordBreak: 'break-word'
                                                                                }}
                                                                            >
                                                                                <i className="fa-solid fa-file mr-1 text-secondary" />
                                                                                {att.originalFileName || att.s3Key}
                                                                            </div>
                                                                            {att.uploadedAt && (
                                                                                <div className="text-muted" style={{ fontSize: '11px' }}>
                                                                                    Uploaded {new Date(att.uploadedAt).toLocaleString()}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="d-flex" style={{ gap: '8px', flexShrink: 0 }}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                onClick={() => downloadOwnerAttachment(att)}
                                                                            >
                                                                                <i className="fa-solid fa-download" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-danger"
                                                                                onClick={() => removeOwnerAttachment(att.s3Key)}
                                                                                disabled={isUploadingAttachments}
                                                                            >
                                                                                <i className="fa-solid fa-trash-can" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {pendingAttachmentFiles.length > 0 && (
                                                        <div className="mb-3">
                                                            <div
                                                                className="fwt_600 mb-2"
                                                                style={{ fontSize: '14px', color: '#003366' }}
                                                            >
                                                                Pending upload
                                                            </div>
                                                            <ul className="list-group">
                                                                {pendingAttachmentFiles.map((file, index) => (
                                                                    <li
                                                                        key={`${file.name}-${index}`}
                                                                        className="list-group-item d-flex justify-content-between align-items-center"
                                                                    >
                                                                        <span style={{ fontSize: '13px' }}>{file.name}</span>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() => removePendingAttachment(index)}
                                                                        >
                                                                            <i className="fa-solid fa-xmark" />
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {activeTab === 'preview' && canAccessPreview && (
                                                <div>
                                                    <div className="card mb-3">
                                                        <div className="card-header fwt_600" style={{ color: '#003366' }}>
                                                            Sales Order Summary
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="row">
                                                                <div className="col-md-4 mb-2">
                                                                    <strong>CPO No:</strong> {orderDetails.cpoNumber || '-'}
                                                                </div>
                                                                <div className="col-md-4 mb-2">
                                                                    <strong>CPO Date:</strong>{' '}
                                                                    {formatDisplayDate(orderDetails.cpoDate)}
                                                                </div>
                                                                <div className="col-md-4 mb-2">
                                                                    <strong>Delivery Date:</strong>{' '}
                                                                    {formatDisplayDate(orderDetails.deliverDate)}
                                                                </div>
                                                                <div className="col-md-4 mb-2">
                                                                    <strong>Customer:</strong> {orderDetails.customerName || '-'}
                                                                </div>
                                                                <div className="col-md-4 mb-2">
                                                                    <strong>Customer Contact Number:</strong>{' '}
                                                                    {orderDetails.customerNumber || '-'}
                                                                </div>
                                                                <div className="col-md-4 mb-2">
                                                                    <strong>Customer Mail ID:</strong>{' '}
                                                                    {orderDetails.customerMailId || '-'}
                                                                </div>
                                                                <div className="col-md-4 mb-2">
                                                                    <strong>Currency:</strong> {orderDetails.currency || '-'}
                                                                </div>
                                                                <div className="col-md-4 mb-2">
                                                                    <strong>Status:</strong> {recordStatus || '-'}
                                                                </div>
                                                                <div className="col-md-12 mb-2">
                                                                    <strong>Summary:</strong> {orderDetails.cpoSummary || '-'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>                                                

                                                    <div className="mb-3" style={{ overflowX: 'auto' }}>
                                                        <table
                                                            className="table table-bordered table-sm mb-0"
                                                            style={{ minWidth: '1100px' }}
                                                        >
                                                            <thead>
                                                                <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                                                    <th>S.No</th>
                                                                    <th>Ref No</th>
                                                                    <th>Material Code</th>
                                                                    <th>Material</th>
                                                                    <th>HSN</th>
                                                                    <th>UOM</th>
                                                                    <th>Qty</th>
                                                                    <th>Basic</th>
                                                                    <th>GST %</th>
                                                                    <th>GST Value</th>
                                                                    <th>Total</th>
                                                                    <th>Remarks</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {filledMaterials.map((row, index) => (
                                                                    <tr key={index}>
                                                                        <td>{index + 1}</td>
                                                                        <td>{row.referenceNumber || '-'}</td>
                                                                        <td>{row.materialCode || '-'}</td>
                                                                        <td
                                                                            style={{
                                                                                maxWidth: '220px',
                                                                                whiteSpace: 'normal',
                                                                                wordBreak: 'break-word'
                                                                            }}
                                                                        >
                                                                            {row.materialDescription || '-'}
                                                                        </td>
                                                                        <td>{row.hsn || '-'}</td>
                                                                        <td>{row.uom || '-'}</td>
                                                                        <td>{row.quantity || '-'}</td>
                                                                        <td>{formatAmount(row.cpoBasicPrice)}</td>
                                                                        <td>{row.gstPercent || '-'}</td>
                                                                        <td>{formatAmount(row.gstValue)}</td>
                                                                        <td>{formatAmount(row.totalValue)}</td>
                                                                        <td
                                                                            style={{
                                                                                maxWidth: '180px',
                                                                                whiteSpace: 'normal',
                                                                                wordBreak: 'break-word'
                                                                            }}
                                                                        >
                                                                            {row.technicalRemarks || '-'}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot>
                                                                <tr>
                                                                    <td
                                                                        colSpan={7}
                                                                        className="text-right fwt_600"
                                                                    >
                                                                        Gross Totals
                                                                    </td>
                                                                    <td className="fwt_600">
                                                                        {formatAmount(
                                                                            materialGrossTotals.grossTotalBasicPrice
                                                                        )}
                                                                    </td>
                                                                    <td />
                                                                    <td className="fwt_600">
                                                                        {formatAmount(
                                                                            materialGrossTotals.grossGstValue
                                                                        )}
                                                                    </td>
                                                                    <td className="fwt_600">
                                                                        {formatAmount(
                                                                            materialGrossTotals.grossTotalValue
                                                                        )}
                                                                    </td>
                                                                    <td />
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>

                                                    <div
                                                        className="mb-3"
                                                        style={{
                                                            border: '1px solid #dee2e6',
                                                            borderRadius: '6px',
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                padding: '10px 14px',
                                                                backgroundColor: '#f8f9fa',
                                                                fontWeight: 600,
                                                                fontSize: '14px'
                                                            }}
                                                        >
                                                            <i className="fa-solid fa-file-pdf mr-2 text-primary" />
                                                            Preview PDF
                                                        </div>
                                                        <div className="p-3">
                                                            {pdfLink ? (
                                                                <>
                                                                    <div className="mb-3">
                                                                        {pdfPreviewUrl ? (
                                                                            <a
                                                                                href={pdfPreviewUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="btn btn-sm btn-outline-primary"
                                                                            >
                                                                                <i className="fa-solid fa-up-right-from-square mr-1" />
                                                                                Open PDF in new tab
                                                                            </a>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                disabled={isLoadingPdfPreview}
                                                                                onClick={() =>
                                                                                    refreshSalesOrderPdf(salesOrderId)
                                                                                }
                                                                            >
                                                                                {isLoadingPdfPreview ? (
                                                                                    <>
                                                                                        <i className="fa-solid fa-spinner fa-spin mr-1" />
                                                                                        Loading PDF...
                                                                                    </>
                                                                                ) : (
                                                                                    'Refresh PDF'
                                                                                )}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            border: '1px solid #dee2e6',
                                                                            borderRadius: '4px',
                                                                            overflow: 'hidden',
                                                                            minHeight: '420px'
                                                                        }}
                                                                    >
                                                                        {isLoadingPdfPreview ? (
                                                                            <div className="text-center py-5 text-muted">
                                                                                <PulseLoader color="#1199B2" size={8} />
                                                                                <div className="mt-2">
                                                                                    Loading PDF preview...
                                                                                </div>
                                                                            </div>
                                                                        ) : pdfPreviewUrl ? (
                                                                            <iframe
                                                                                title="Sales Order PDF Preview"
                                                                                src={pdfPreviewUrl}
                                                                                style={{
                                                                                    width: '100%',
                                                                                    height: '520px',
                                                                                    border: 'none'
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <p className="text-muted mb-0 p-3">
                                                                                Could not load PDF preview. Try refresh
                                                                                shortly.
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="text-muted">
                                                                    <p className="mb-2">
                                                                        PDF is being generated after activation. It will
                                                                        appear here once available.
                                                                    </p>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        disabled={isLoadingPdfPreview}
                                                                        onClick={async () => {
                                                                            setIsLoadingPdfPreview(true)
                                                                            await refreshSalesOrderPdf(salesOrderId)
                                                                            setIsLoadingPdfPreview(false)
                                                                        }}
                                                                    >
                                                                        {isLoadingPdfPreview ? (
                                                                            <>
                                                                                <i className="fa-solid fa-spinner fa-spin mr-1" />
                                                                                Checking...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <i className="fa-solid fa-rotate mr-1" />
                                                                                Check for PDF
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {ownerAttachments.length > 0 && (
                                                        <div className="mb-3">
                                                            <div className="fwt_600 mb-2" style={{ color: '#003366' }}>
                                                                Attachments
                                                            </div>
                                                            <ul className="mb-0">
                                                                {ownerAttachments.map((att) => (
                                                                    <li key={att.s3Key}>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-link p-0"
                                                                            onClick={() => downloadOwnerAttachment(att)}
                                                                        >
                                                                            {att.originalFileName || att.s3Key}
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}

                                                    {terms.length > 0 && (
                                                        <div className="mb-3">
                                                            <div className="fwt_600 mb-2" style={{ color: '#003366' }}>
                                                                Terms
                                                            </div>
                                                            <table className="table table-bordered table-sm mb-0">
                                                                <thead>
                                                                    <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                                                        <th>Term</th>
                                                                        <th>Response</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {terms.map((row, index) => (
                                                                        <tr key={index}>
                                                                            <td>{row.term || '-'}</td>
                                                                            <td style={{ whiteSpace: 'pre-wrap' }}>
                                                                                {row.response || '-'}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}

<div className="card mb-3">
                                                        <div
                                                            className="card-header fwt_600 d-flex align-items-center"
                                                            style={{ color: '#003366' }}
                                                        >
                                                            <i className="fa-solid fa-envelope mr-2" />
                                                            Send Sales Order Email
                                                        </div>
                                                        <div className="card-body">
                                                            <div className="row">
                                                                <div className="col-md-6 mb-3">
                                                                    <label className="mb-1">To Email</label>
                                                                    <div
                                                                        className="form-control form-control-sm bg-light"
                                                                        style={{
                                                                            minHeight: '31px',
                                                                            height: 'auto'
                                                                        }}
                                                                    >
                                                                        {sendToEmail ||
                                                                            orderDetails.customerMailId || (
                                                                                <span className="text-muted">
                                                                                    No customer email
                                                                                </span>
                                                                            )}
                                                                    </div>
                                                                    <small className="form-text text-muted">
                                                                        Customer mail ID from the order.
                                                                    </small>
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <label className="mb-1">
                                                                        CC Emails (your email)
                                                                    </label>
                                                                    <div
                                                                        className="form-control form-control-sm bg-light"
                                                                        style={{
                                                                            minHeight: '31px',
                                                                            height: 'auto'
                                                                        }}
                                                                    >
                                                                        {userEmail || (
                                                                            <span className="text-muted">
                                                                                Profile email not available —
                                                                                add CC addresses below
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <small className="form-text text-muted">
                                                                        Always included on send.
                                                                    </small>
                                                                </div>
                                                                <div className="col-md-12 mb-3">
                                                                    <label className="mb-1">
                                                                        Additional CC Emails
                                                                    </label>
                                                                    <div style={{ position: 'relative' }}>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control form-control-sm"
                                                                            placeholder="Type name or email to search employees"
                                                                            value={additionalCcEmails}
                                                                            onChange={handleAdditionalCcChange}
                                                                            onBlur={handleAdditionalCcBlur}
                                                                            disabled={isSendingEmail}
                                                                            autoComplete="off"
                                                                        />
                                                                        {(isSearchingCcUsers ||
                                                                            ccUserSuggestions.length > 0) && (
                                                                            <div
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    top: '100%',
                                                                                    left: 0,
                                                                                    right: 0,
                                                                                    zIndex: 1060,
                                                                                    backgroundColor: '#fff',
                                                                                    border: '1px solid #d8e0ea',
                                                                                    borderRadius: '8px',
                                                                                    boxShadow:
                                                                                        '0 10px 24px rgba(15, 23, 42, 0.14)',
                                                                                    maxHeight: '210px',
                                                                                    overflowY: 'auto',
                                                                                    marginTop: '2px'
                                                                                }}
                                                                            >
                                                                                {isSearchingCcUsers ? (
                                                                                    <div
                                                                                        className="text-muted"
                                                                                        style={{
                                                                                            padding: '10px 12px',
                                                                                            fontSize: '13px'
                                                                                        }}
                                                                                    >
                                                                                        <i className="fa-solid fa-spinner fa-spin mr-2" />
                                                                                        Searching employees...
                                                                                    </div>
                                                                                ) : (
                                                                                    (() => {
                                                                                        const existing = new Set(
                                                                                            buildCcEmails(
                                                                                                userEmail,
                                                                                                additionalCcEmails,
                                                                                                sendToEmail ||
                                                                                                    orderDetails.customerMailId
                                                                                            ).map((item) =>
                                                                                                item.toLowerCase()
                                                                                            )
                                                                                        )
                                                                                        const visible =
                                                                                            ccUserSuggestions.filter(
                                                                                                (item) =>
                                                                                                    !existing.has(
                                                                                                        String(
                                                                                                            item.email
                                                                                                        )
                                                                                                            .trim()
                                                                                                            .toLowerCase()
                                                                                                    )
                                                                                            )
                                                                                        if (visible.length === 0) {
                                                                                            return (
                                                                                                <div
                                                                                                    className="text-muted"
                                                                                                    style={{
                                                                                                        padding:
                                                                                                            '10px 12px',
                                                                                                        fontSize: '13px'
                                                                                                    }}
                                                                                                >
                                                                                                    No matching employees
                                                                                                    found
                                                                                                </div>
                                                                                            )
                                                                                        }
                                                                                        return visible.map((item) => (
                                                                                            <button
                                                                                                key={item.email}
                                                                                                type="button"
                                                                                                className="dropdown-item"
                                                                                                style={{
                                                                                                    display: 'block',
                                                                                                    width: '100%',
                                                                                                    textAlign: 'left',
                                                                                                    padding: '8px 12px',
                                                                                                    whiteSpace: 'normal',
                                                                                                    cursor: 'pointer'
                                                                                                }}
                                                                                                onMouseDown={(e) => {
                                                                                                    e.preventDefault()
                                                                                                    handleSelectCcUser(
                                                                                                        item.email
                                                                                                    )
                                                                                                }}
                                                                                            >
                                                                                                <span
                                                                                                    className="d-block fwt_600"
                                                                                                    style={{
                                                                                                        fontSize: '13px'
                                                                                                    }}
                                                                                                >
                                                                                                    {item.name ||
                                                                                                        item.email}
                                                                                                </span>
                                                                                                <small className="text-muted d-block">
                                                                                                    {item.email}
                                                                                                </small>
                                                                                            </button>
                                                                                        ))
                                                                                    })()
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <small className="form-text text-muted">
                                                                        Type at least 3 characters to search internal
                                                                        employees. Comma or semicolon separated.
                                                                    </small>
                                                                    {buildCcEmails(
                                                                        userEmail,
                                                                        additionalCcEmails,
                                                                        sendToEmail || orderDetails.customerMailId
                                                                    ).length > 0 && (
                                                                        <small className="form-text text-muted d-block">
                                                                            Will CC:{' '}
                                                                            {buildCcEmails(
                                                                                userEmail,
                                                                                additionalCcEmails,
                                                                                sendToEmail ||
                                                                                    orderDetails.customerMailId
                                                                            ).join(', ')}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-blue btn-sm"
                                                                onClick={handleSendSalesOrderEmail}
                                                                disabled={
                                                                    isSendingEmail ||
                                                                    !String(
                                                                        sendToEmail ||
                                                                            orderDetails.customerMailId ||
                                                                            ''
                                                                    ).trim() ||
                                                                    !String(pdfLink || '').trim() ||
                                                                    String(recordStatus || '').toLowerCase() !==
                                                                        'active'
                                                                }
                                                            >
                                                                {isSendingEmail ? (
                                                                    <>
                                                                        <i className="fa-solid fa-spinner fa-spin mr-1" />
                                                                        {editId ? 'Resending...' : 'Sending...'}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="fa-solid fa-paper-plane mr-1" />
                                                                        {editId ? 'Resend Email' : 'Send Email'}
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="card-footer d-flex justify-content-center align-items-center">
                                        {!isLoadingOrder && activeTab === 'details' && (
                                            <button
                                                type="button"
                                                className="btn btn-blue"
                                                onClick={() => goToTab('materials')}
                                                disabled={isLoading}
                                            >
                                                Next
                                            </button>
                                        )}

                                        {!isLoadingOrder && activeTab === 'materials' && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="btn btn-blue mr-2"
                                                    onClick={() => goToTab('details')}
                                                    disabled={isLoading}
                                                >
                                                    Previous
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-blue"
                                                    onClick={() => goToTab('attachments')}
                                                    disabled={isLoading}
                                                >
                                                    Next
                                                </button>
                                            </>
                                        )}

                                        {!isLoadingOrder && activeTab === 'attachments' && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="btn btn-blue mr-2"
                                                    onClick={() => goToTab('materials')}
                                                    disabled={isLoading || isUploadingAttachments}
                                                >
                                                    Previous
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-blue"
                                                    onClick={async () => {
                                                        if (!(await saveTabAttachments())) return
                                                        await activateSalesOrder()
                                                    }}
                                                    disabled={isLoading || isUploadingAttachments}
                                                >
                                                    {isUploadingAttachments
                                                        ? 'Uploading...'
                                                        : isOrderActive
                                                            ? 'Update & Preview'
                                                            : 'Activate & Preview'}
                                                </button>
                                            </>
                                        )}

                                        {!isLoadingOrder && activeTab === 'preview' && (
                                            <button
                                                type="button"
                                                className="btn btn-blue"
                                                onClick={() => goToTab('attachments')}
                                                disabled={isLoading}
                                            >
                                                Previous
                                            </button>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SalesOrderCreate